'use strict'

import {
  startPortalLoadingTask
} from './portal-loading-widget.js'
import {
  buildGeoTiffDescriptor,
  readGeoTiffCacheEntry,
  readGeoTiffTerrainMeta,
  writeGeoTiffTerrainMeta,
  writeGeoTiffActiveDescriptor
} from './portal-geotiff-cache.js'
import {
  offsetLatLngByMeters,
  bearingBetweenLatLng
} from './portal-geo-utils.js'
import {
  decorateLeafletPathDualDash
} from './portal-dual-dash.js'

const PORTAL_LOCAL_TERRAIN_STATE_KEY = 'PORTAL_LOCAL_TERRAIN_STATE'
const LOCAL_DEM_PROVIDER_KEY = 'localDEM'
const LOCAL_TERRAIN_COVERAGE_INSET_METERS = 3.5
const TERRAIN_CALIBRATION_SAMPLE_COUNT = 1000
const TERRAIN_CALIBRATION_MAX_SAMPLE_COUNT = 1000
const TERRAIN_CALIBRATION_PROVIDER_BATCH_SIZE = 120
const TIFF_VERTICAL_REFERENCE_NONE = 'none'
const TIFF_VERTICAL_REFERENCE_MAP = {
  none: 'Nincs explicit vertikális referencia',
  custom_vertical: 'TIFF explicit vertikális CRS',
  wgs84_grs80_ellipsoid: 'WGS84/GRS80 ellipszoid (h)',
  egm96_geoid: 'EGM96 geoid (H, ASL)',
  egm2008_geoid: 'EGM2008 geoid (H, ASL)',
  msl_unspecified: 'MSL (modell nincs megadva)',
  evrf2007: 'EVRF2007',
  evrf2019: 'EVRF2019',
  baltic_1977: 'Baltic 1977',
  navd88: 'NAVD88',
  ngvd29: 'NGVD29',
  odn: 'ODN (Ordnance Datum Newlyn)',
  cgvd2013: 'CGVD2013'
}

const fnNormalizeTiffVerticalReference = function (rawValue, fallbackKey) {
  const fallback = String(fallbackKey || '').trim() || TIFF_VERTICAL_REFERENCE_NONE
  const key = String(rawValue || '').trim()
  if (!key) return fallback
  if (Object.prototype.hasOwnProperty.call(TIFF_VERTICAL_REFERENCE_MAP, key)) return key
  return fallback
}

const fnGetTiffVerticalReferenceLabel = function (referenceKey) {
  const key = fnNormalizeTiffVerticalReference(referenceKey, TIFF_VERTICAL_REFERENCE_NONE)
  return String(TIFF_VERTICAL_REFERENCE_MAP[key] || TIFF_VERTICAL_REFERENCE_MAP[TIFF_VERTICAL_REFERENCE_NONE])
}

const fnMatchVerticalReferenceKeyFromText = function (rawText) {
  const text = String(rawText || '').toLowerCase()
  if (!text) return ''
  if (text.includes('egm2008')) return 'egm2008_geoid'
  if (text.includes('egm96')) return 'egm96_geoid'
  if (text.includes('ellipsoid')) return 'wgs84_grs80_ellipsoid'
  if (text.includes('evrf2019')) return 'evrf2019'
  if (text.includes('evrf2007')) return 'evrf2007'
  if (text.includes('baltic')) return 'baltic_1977'
  if (text.includes('navd88')) return 'navd88'
  if (text.includes('ngvd29')) return 'ngvd29'
  if (text.includes('newlyn') || text.includes('odn')) return 'odn'
  if (text.includes('cgvd2013')) return 'cgvd2013'
  if (text.includes('mean sea level') || text.includes('msl') || text.includes('orthometric') || text.includes('geoid')) {
    return 'msl_unspecified'
  }
  return ''
}

const fnExtractVerticalReferenceFromGeoKeyDirectory = function (directoryRaw) {
  let directory = directoryRaw
  if (ArrayBuffer.isView(directory)) {
    directory = Array.from(directory)
  }
  if (!Array.isArray(directory) || directory.length < 8) return null
  const keyCount = Number(directory[3])
  if (!Number.isFinite(keyCount) || keyCount < 1) return null
  let verticalCSType = null
  for (let i = 0; i < keyCount; i++) {
    const base = 4 + (i * 4)
    if (base + 3 >= directory.length) break
    const keyId = Number(directory[base])
    const tiffTagLocation = Number(directory[base + 1])
    const count = Number(directory[base + 2])
    const valueOffset = Number(directory[base + 3])
    if (keyId === 4096 && tiffTagLocation === 0 && count === 1 && Number.isFinite(valueOffset)) {
      verticalCSType = Math.trunc(valueOffset)
      break
    }
  }
  if (!Number.isFinite(verticalCSType) || verticalCSType <= 0) return null
  return fnBuildVerticalReferenceFromCSType(verticalCSType, 'geokey_directory_verticalcstype')
}

const fnBuildVerticalReferenceFromCSType = function (verticalCSTypeRaw, source) {
  const verticalCSType = Number(verticalCSTypeRaw)
  if (!Number.isFinite(verticalCSType) || verticalCSType <= 0) return null
  const epsg = Math.trunc(verticalCSType)
  const keyByEpsg = {
    5773: 'egm96_geoid',
    3855: 'egm2008_geoid',
    5703: 'navd88',
    5702: 'ngvd29',
    6647: 'cgvd2013',
    5101: 'odn'
  }
  const mapped = String(keyByEpsg[epsg] || '')
  if (mapped) {
    return {
      detected: true,
      key: mapped,
      label: fnGetTiffVerticalReferenceLabel(mapped),
      source: String(source || 'vertical_cstype'),
      raw: epsg
    }
  }
  return {
    detected: true,
    key: 'custom_vertical',
    label: 'EPSG:' + String(epsg) + ' (vertical)',
    source: String(source || 'vertical_cstype'),
    raw: epsg
  }
}

const fnBuildVerticalMetadataTextCandidates = function (georaster) {
  const out = []
  const addCandidate = function (source, value) {
    if (value === undefined || value === null) return
    const text = String(value).trim()
    if (!text) return
    out.push({
      source: String(source || ''),
      text: text
    })
  }
  if (!georaster || typeof georaster !== 'object') return out
  addCandidate('geoKeys.VerticalCitationGeoKey', georaster.geoKeys && georaster.geoKeys.VerticalCitationGeoKey)
  addCandidate('geoKeys.VerticalDatumGeoKey', georaster.geoKeys && georaster.geoKeys.VerticalDatumGeoKey)
  addCandidate('metadata.VerticalCitationGeoKey', georaster.metadata && georaster.metadata.VerticalCitationGeoKey)
  addCandidate('fileDirectory.VerticalCitationGeoKey', georaster.fileDirectory && georaster.fileDirectory.VerticalCitationGeoKey)
  addCandidate('metadata.GDAL_METADATA', georaster.metadata && georaster.metadata.GDAL_METADATA)
  addCandidate('fileDirectory.GDAL_METADATA', georaster.fileDirectory && georaster.fileDirectory.GDAL_METADATA)
  addCandidate('metadata.GDALMetadata', georaster.metadata && georaster.metadata.GDALMetadata)
  addCandidate('fileDirectory.GDALMetadata', georaster.fileDirectory && georaster.fileDirectory.GDALMetadata)
  addCandidate('metadata.GeoAsciiParamsTag', georaster.metadata && georaster.metadata.GeoAsciiParamsTag)
  addCandidate('fileDirectory.GeoAsciiParamsTag', georaster.fileDirectory && georaster.fileDirectory.GeoAsciiParamsTag)
  return out
}

const fnDetectTiffVerticalReference = function (georaster) {
  const unknown = {
    detected: false,
    key: TIFF_VERTICAL_REFERENCE_NONE,
    label: fnGetTiffVerticalReferenceLabel(TIFF_VERTICAL_REFERENCE_NONE),
    source: 'none',
    raw: null
  }
  if (!georaster || typeof georaster !== 'object') return unknown

  const geoKeyDirectoryCandidates = [
    georaster.GeoKeyDirectoryTag,
    georaster.geoKeyDirectory,
    georaster.geoKeysDirectory,
    georaster.fileDirectory && georaster.fileDirectory.GeoKeyDirectoryTag,
    georaster.metadata && georaster.metadata.GeoKeyDirectoryTag
  ]
  for (let i = 0; i < geoKeyDirectoryCandidates.length; i++) {
    const match = fnExtractVerticalReferenceFromGeoKeyDirectory(geoKeyDirectoryCandidates[i])
    if (match && match.detected) return match
  }

  const directVerticalCandidates = [
    georaster.VerticalCSTypeGeoKey,
    georaster.VerticalUnitsGeoKey,
    georaster.geoKeys && georaster.geoKeys.VerticalCSTypeGeoKey,
    georaster.metadata && georaster.metadata.VerticalCSTypeGeoKey,
    georaster.fileDirectory && georaster.fileDirectory.VerticalCSTypeGeoKey
  ]
  for (let i = 0; i < directVerticalCandidates.length; i++) {
    const match = fnBuildVerticalReferenceFromCSType(directVerticalCandidates[i], 'vertical_cstype_field')
    if (match && match.detected) return match
  }

  const textCandidates = fnBuildVerticalMetadataTextCandidates(georaster)
  for (let i = 0; i < textCandidates.length; i++) {
    const row = textCandidates[i]
    const key = fnMatchVerticalReferenceKeyFromText(row.text)
    if (!key) continue
    return {
      detected: true,
      key: key,
      label: fnGetTiffVerticalReferenceLabel(key),
      source: 'metadata_text:' + String(row.source || ''),
      raw: row.text
    }
  }

  return unknown
}

const fnToFiniteNumber = function (value) {
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

const fnGetBandValueAt = function (bandData, row, col, width, height) {
  if (!bandData || !Number.isFinite(row) || !Number.isFinite(col)) return null
  const r = Math.max(0, Math.min(height - 1, Math.trunc(row)))
  const c = Math.max(0, Math.min(width - 1, Math.trunc(col)))
  if (ArrayBuffer.isView(bandData)) {
    const idx = (r * width) + c
    if (idx < 0 || idx >= bandData.length) return null
    return fnToFiniteNumber(bandData[idx])
  }
  if (!Array.isArray(bandData)) return null
  const rowData = bandData[r]
  if (!rowData) return null
  if (ArrayBuffer.isView(rowData)) {
    if (c < 0 || c >= rowData.length) return null
    return fnToFiniteNumber(rowData[c])
  }
  if (Array.isArray(rowData)) {
    if (c < 0 || c >= rowData.length) return null
    return fnToFiniteNumber(rowData[c])
  }
  return null
}

const fnBuildConvexHull = function (points) {
  const pts = (Array.isArray(points) ? points : [])
    .filter((pt) => pt && Number.isFinite(pt.x) && Number.isFinite(pt.y))
    .sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x))
  if (pts.length < 3) return pts
  const cross = function (o, a, b) {
    return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)
  }
  const lower = []
  for (let i = 0; i < pts.length; i++) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], pts[i]) <= 0) {
      lower.pop()
    }
    lower.push(pts[i])
  }
  const upper = []
  for (let i = pts.length - 1; i >= 0; i--) {
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], pts[i]) <= 0) {
      upper.pop()
    }
    upper.push(pts[i])
  }
  upper.pop()
  lower.pop()
  return lower.concat(upper)
}

const fnNormalizeEpsgCode = function (projection) {
  const raw = String(projection === undefined || projection === null ? '' : projection).trim()
  if (!raw) return null
  const match = raw.match(/(\d{3,6})/)
  if (!match) return null
  const code = Number(match[1])
  return Number.isFinite(code) ? code : null
}

const fnIsLatLngInRange = function (lat, lng) {
  const latNum = Number(lat)
  const lngNum = Number(lng)
  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return false
  return latNum >= -90 && latNum <= 90 && lngNum >= -180 && lngNum <= 180
}

const fnAreLatLngPolygonSane = function (latLngs) {
  if (!Array.isArray(latLngs) || latLngs.length < 3) return false
  for (let i = 0; i < latLngs.length; i++) {
    const row = latLngs[i]
    if (!Array.isArray(row) || row.length < 2) return false
    if (!fnIsLatLngInRange(row[0], row[1])) return false
  }
  return true
}

const fnIsPointInsideLatLngPolygon = function (lat, lng, latLngs) {
  const latNum = Number(lat)
  const lngNum = Number(lng)
  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return false
  if (!fnAreLatLngPolygonSane(latLngs)) return false
  let inside = false
  for (let i = 0, j = latLngs.length - 1; i < latLngs.length; j = i++) {
    const rowI = latLngs[i]
    const rowJ = latLngs[j]
    const latI = Number(rowI[0])
    const lngI = Number(rowI[1])
    const latJ = Number(rowJ[0])
    const lngJ = Number(rowJ[1])
    const intersects = ((latI > latNum) !== (latJ > latNum)) &&
      (lngNum < ((lngJ - lngI) * (latNum - latI) / ((latJ - latI) || 1e-12)) + lngI)
    if (intersects) inside = !inside
  }
  return inside
}

const fnShouldAutoFitTerrainFootprint = function (latLngs) {
  if (!fnAreLatLngPolygonSane(latLngs)) return false
  let minLat = Infinity
  let maxLat = -Infinity
  let minLng = Infinity
  let maxLng = -Infinity
  for (let i = 0; i < latLngs.length; i++) {
    const row = latLngs[i]
    const lat = Number(row[0])
    const lng = Number(row[1])
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
  }
  const latSpan = maxLat - minLat
  const lngSpan = maxLng - minLng
  return latSpan > 0 && lngSpan > 0 && latSpan <= 5 && lngSpan <= 5
}

const fnEnsureProj4DefinitionForEpsg = function (epsg) {
  if (typeof window === 'undefined' || !window.proj4 || typeof window.proj4.defs !== 'function') return false
  const code = Number(epsg)
  if (!Number.isFinite(code)) return false
  const crs = 'EPSG:' + String(Math.trunc(code))
  const existing = window.proj4.defs(crs)
  if (existing) return true

  // WGS84 UTM north
  if (code >= 32601 && code <= 32660) {
    const zone = Math.trunc(code - 32600)
    window.proj4.defs(crs, '+proj=utm +zone=' + String(zone) + ' +datum=WGS84 +units=m +no_defs +type=crs')
    return !!window.proj4.defs(crs)
  }
  // WGS84 UTM south
  if (code >= 32701 && code <= 32760) {
    const zone = Math.trunc(code - 32700)
    window.proj4.defs(crs, '+proj=utm +zone=' + String(zone) + ' +south +datum=WGS84 +units=m +no_defs +type=crs')
    return !!window.proj4.defs(crs)
  }
  return false
}

const fnGetLatLngPolygonMetrics = function (latLngs) {
  if (!Array.isArray(latLngs) || !latLngs.length) {
    return {
      sane: false,
      count: 0,
      minLat: null,
      maxLat: null,
      minLng: null,
      maxLng: null,
      latSpan: null,
      lngSpan: null
    }
  }
  if (!fnAreLatLngPolygonSane(latLngs)) {
    return {
      sane: false,
      count: latLngs.length,
      minLat: null,
      maxLat: null,
      minLng: null,
      maxLng: null,
      latSpan: null,
      lngSpan: null
    }
  }
  let minLat = Infinity
  let maxLat = -Infinity
  let minLng = Infinity
  let maxLng = -Infinity
  for (let i = 0; i < latLngs.length; i++) {
    const row = latLngs[i]
    const lat = Number(row[0])
    const lng = Number(row[1])
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
  }
  return {
    sane: true,
    count: latLngs.length,
    minLat,
    maxLat,
    minLng,
    maxLng,
    latSpan: maxLat - minLat,
    lngSpan: maxLng - minLng
  }
}

const fnBuildGeoCoordTransform = function (georaster) {
  const epsg = fnNormalizeEpsgCode(georaster && georaster.projection)
  const projectionIsGeographic = !epsg || epsg === 4326
  if (projectionIsGeographic) {
    return {
      epsg: epsg || 4326,
      projectionIsGeographic: true,
      projectionTransformAvailable: true,
      hasProj4: true,
      sourceCrsDefined: true,
      sourceCrsAutoDefined: false,
      sourceToLatLng: function (x, y) { return { lat: Number(y), lng: Number(x) } },
      latLngToSource: function (lat, lng) { return { x: Number(lng), y: Number(lat) } }
    }
  }
  const hasProj4 = (typeof window !== 'undefined' && typeof window.proj4 === 'function')
  if (!hasProj4) {
    return {
      epsg,
      projectionIsGeographic: false,
      projectionTransformAvailable: false,
      hasProj4: false,
      sourceCrsDefined: false,
      sourceCrsAutoDefined: false,
      sourceToLatLng: function () { return null },
      latLngToSource: function () { return null }
    }
  }
  const srcCrs = 'EPSG:' + String(epsg)
  const srcDefBefore = !!window.proj4.defs(srcCrs)
  const ensured = fnEnsureProj4DefinitionForEpsg(epsg)
  const srcDefAfter = !!window.proj4.defs(srcCrs)
  if (!srcDefAfter) {
    return {
      epsg,
      projectionIsGeographic: false,
      projectionTransformAvailable: false,
      hasProj4: true,
      sourceCrsDefined: false,
      sourceCrsAutoDefined: ensured && !srcDefBefore,
      sourceToLatLng: function () { return null },
      latLngToSource: function () { return null }
    }
  }
  const dstCrs = 'EPSG:4326'
  return {
    epsg,
    projectionIsGeographic: false,
    projectionTransformAvailable: true,
    hasProj4: true,
    sourceCrsDefined: true,
    sourceCrsAutoDefined: ensured && !srcDefBefore,
    sourceToLatLng: function (x, y) {
      try {
        const xy = window.proj4(srcCrs, dstCrs, [Number(x), Number(y)])
        if (!Array.isArray(xy) || xy.length < 2) return null
        const lng = Number(xy[0])
        const lat = Number(xy[1])
        if (!fnIsLatLngInRange(lat, lng)) return null
        return { lat, lng }
      } catch (_error) {
        return null
      }
    },
    latLngToSource: function (lat, lng) {
      try {
        const xy = window.proj4(dstCrs, srcCrs, [Number(lng), Number(lat)])
        if (!Array.isArray(xy) || xy.length < 2) return null
        const x = Number(xy[0])
        const y = Number(xy[1])
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null
        return { x, y }
      } catch (_error) {
        return null
      }
    }
  }
}

const fnBuildBandStats = function (georaster, bandIndex) {
  const width = Math.max(1, Number(georaster && georaster.width) || 0)
  const height = Math.max(1, Number(georaster && georaster.height) || 0)
  const values = georaster && georaster.values
  const bandData = (Array.isArray(values) && values.length > bandIndex) ? values[bandIndex] : null
  if (!bandData) {
    return { finiteCount: 0, uniqueCount: 0, min: null, max: null, range: 0, binaryLike: false }
  }
  const rowStep = Math.max(1, Math.floor(height / 128))
  const colStep = Math.max(1, Math.floor(width / 128))
  const uniques = new Set()
  let finiteCount = 0
  let min = null
  let max = null
  for (let row = 0; row < height; row += rowStep) {
    for (let col = 0; col < width; col += colStep) {
      const v = fnGetBandValueAt(bandData, row, col, width, height)
      if (v === null) continue
      finiteCount += 1
      if (min === null || v < min) min = v
      if (max === null || v > max) max = v
      if (uniques.size < 32) {
        uniques.add(String(Math.round(v * 1000) / 1000))
      }
    }
  }
  const uniqueCount = uniques.size
  const range = (min === null || max === null) ? 0 : (max - min)
  const binaryLike = uniqueCount > 0 && uniqueCount <= 4 && range <= 255
  return { finiteCount, uniqueCount, min, max, range, binaryLike }
}

const fnEstimateBandByteLength = function (bandData) {
  if (!bandData) return 0
  if (ArrayBuffer.isView(bandData)) {
    return Number(bandData.byteLength) || 0
  }
  if (!Array.isArray(bandData)) return 0
  let total = 0
  for (let i = 0; i < bandData.length; i++) {
    total += fnEstimateBandByteLength(bandData[i])
  }
  return total
}

const fnFormatBytes = function (bytes) {
  const n = Number(bytes)
  if (!Number.isFinite(n) || n <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let value = n
  let idx = 0
  while (value >= 1024 && idx < units.length - 1) {
    value /= 1024
    idx += 1
  }
  const decimals = value >= 100 ? 0 : (value >= 10 ? 1 : 2)
  return value.toFixed(decimals) + ' ' + units[idx]
}

const fnEstimateTerrainStorage = function (georaster, localTerrainState) {
  const values = georaster && Array.isArray(georaster.values) ? georaster.values : []
  let decodedRasterBytes = 0
  for (let i = 0; i < values.length; i++) {
    decodedRasterBytes += fnEstimateBandByteLength(values[i])
  }

  const heightBandIndex = Number(localTerrainState && localTerrainState.heightBandIndex)
  const maskBandIndex = Number(localTerrainState && localTerrainState.maskBandIndex)
  const selectedBandIndexes = []
  if (Number.isFinite(heightBandIndex) && heightBandIndex >= 0) selectedBandIndexes.push(heightBandIndex)
  if (Number.isFinite(maskBandIndex) && maskBandIndex >= 0 && !selectedBandIndexes.includes(maskBandIndex)) {
    selectedBandIndexes.push(maskBandIndex)
  }

  let selectedBandsBytes = 0
  for (let i = 0; i < selectedBandIndexes.length; i++) {
    const idx = selectedBandIndexes[i]
    selectedBandsBytes += fnEstimateBandByteLength(values[idx])
  }

  const footprint = (localTerrainState && Array.isArray(localTerrainState.footprintLatLngs))
    ? localTerrainState.footprintLatLngs
    : []
  const footprintBytes = footprint.length * 2 * 8

  let metadataBytes = 0
  try {
    const meta = {
      width: Number(georaster && georaster.width) || 0,
      height: Number(georaster && georaster.height) || 0,
      projection: georaster ? georaster.projection : null,
      xmin: georaster ? georaster.xmin : null,
      xmax: georaster ? georaster.xmax : null,
      ymin: georaster ? georaster.ymin : null,
      ymax: georaster ? georaster.ymax : null,
      noDataValue: georaster ? georaster.noDataValue : null,
      heightBandIndex: Number.isFinite(heightBandIndex) ? heightBandIndex : -1,
      maskBandIndex: Number.isFinite(maskBandIndex) ? maskBandIndex : -1,
      projectionIsGeographic: !!(localTerrainState && localTerrainState.projectionIsGeographic),
      projectionEpsg: localTerrainState ? localTerrainState.projectionEpsg : null
    }
    const json = JSON.stringify(meta)
    if (typeof TextEncoder === 'function') {
      metadataBytes = new TextEncoder().encode(json).byteLength
    } else {
      metadataBytes = String(json || '').length * 2
    }
  } catch (_error) {}

  const estimatedIndexedDbBytes = selectedBandsBytes + footprintBytes + metadataBytes
  return {
    decodedRasterBytes,
    decodedRasterHuman: fnFormatBytes(decodedRasterBytes),
    selectedBandsBytes,
    selectedBandsHuman: fnFormatBytes(selectedBandsBytes),
    footprintBytes,
    metadataBytes,
    estimatedIndexedDbBytes,
    estimatedIndexedDbHuman: fnFormatBytes(estimatedIndexedDbBytes),
    selectedBandIndexes
  }
}

const fnMedian = function (values) {
  const nums = (Array.isArray(values) ? values : [])
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v))
    .sort((a, b) => a - b)
  if (!nums.length) return null
  const mid = Math.floor(nums.length / 2)
  if (nums.length % 2 === 1) return nums[mid]
  return (nums[mid - 1] + nums[mid]) / 2
}

const fnBuildTerrainCalibrationProviderOrder = function () {
  if (typeof window === 'undefined') return ['openhikingHungary', 'gDEM', 'omeDEM', 'oeDEM', 'otdDEM']
  const fnGetProvider = (typeof window.PORTAL_GET_MAP_CURSOR_ELEVATION_PROVIDER === 'function')
    ? window.PORTAL_GET_MAP_CURSOR_ELEVATION_PROVIDER
    : function () { return 'openhikingHungary' }
  const fnGetFallbackProvider = (typeof window.PORTAL_GET_MAP_CURSOR_ELEVATION_FALLBACK_PROVIDER === 'function')
    ? window.PORTAL_GET_MAP_CURSOR_ELEVATION_FALLBACK_PROVIDER
    : function () { return 'openhikingHungary' }
  const fnBuildTryOrder = (typeof window.PORTAL_BUILD_MAP_CURSOR_ELEVATION_TRY_ORDER === 'function')
    ? window.PORTAL_BUILD_MAP_CURSOR_ELEVATION_TRY_ORDER
    : function (providerKey) { return [String(providerKey || 'openhikingHungary')] }
  const fnIsProviderEnabled = (typeof window.PORTAL_IS_DEM_PROVIDER_ENABLED === 'function')
    ? window.PORTAL_IS_DEM_PROVIDER_ENABLED
    : function () { return true }

  const primary = String(fnGetProvider() || '').trim() || 'openhikingHungary'
  const fallback = String(fnGetFallbackProvider() || '').trim() || 'openhikingHungary'
  const startProvider = (primary === LOCAL_DEM_PROVIDER_KEY)
    ? (fallback || 'openhikingHungary')
    : primary

  const rawOrder = []
    .concat([startProvider])
    .concat(fnBuildTryOrder(startProvider))
    .concat(['openhikingHungary', 'gDEM', 'omeDEM', 'oeDEM', 'otdDEM', 'arcgisEle'])
  const seen = {}
  const out = []
  rawOrder.forEach((providerKeyRaw) => {
    const providerKey = String(providerKeyRaw || '').trim()
    if (!providerKey || providerKey === LOCAL_DEM_PROVIDER_KEY) return
    if (seen[providerKey]) return
    seen[providerKey] = true
    if (!fnIsProviderEnabled(providerKey)) return
    out.push(providerKey)
  })
  return out
}

const fnBuildTerrainCalibrationSamples = async function (localTerrainState, sampleCount) {
  const requested = Math.max(
    10,
    Math.min(TERRAIN_CALIBRATION_MAX_SAMPLE_COUNT, Math.trunc(Number(sampleCount) || TERRAIN_CALIBRATION_SAMPLE_COUNT))
  )
  if (
    !localTerrainState ||
    (
      typeof localTerrainState.sampleElevationRaw !== 'function' &&
      typeof localTerrainState.sampleElevationsRaw !== 'function'
    )
  ) {
    return []
  }
  const footprint = Array.isArray(localTerrainState.footprintLatLngs) ? localTerrainState.footprintLatLngs : []
  const metrics = fnGetLatLngPolygonMetrics(footprint)
  if (!metrics.sane || !(metrics.latSpan > 0) || !(metrics.lngSpan > 0)) return []
  const minLat = Number(metrics.minLat)
  const maxLat = Number(metrics.maxLat)
  const minLng = Number(metrics.minLng)
  const maxLng = Number(metrics.maxLng)
  if (![minLat, maxLat, minLng, maxLng].every((n) => Number.isFinite(n))) return []

  const samples = []
  const fnContainsLatLng = function (lat, lng) {
    if (typeof localTerrainState.containsLatLng === 'function') {
      return localTerrainState.containsLatLng(lat, lng) === true
    }
    return true
  }
  const fnAppendBatchSamples = async function (batchPoints) {
    const points = (Array.isArray(batchPoints) ? batchPoints : []).filter(function (point) {
      return Number.isFinite(Number(point && point.lat)) && Number.isFinite(Number(point && point.lng))
    })
    if (!points.length || samples.length >= requested) return
    if (typeof localTerrainState.sampleElevationRaw === 'function') {
      for (let idx = 0; idx < points.length && samples.length < requested; idx += 1) {
        const point = points[idx]
        const localElevationRaw = Number(localTerrainState.sampleElevationRaw(point.lat, point.lng))
        if (!Number.isFinite(localElevationRaw)) continue
        samples.push({
          lat: Number(point.lat),
          lng: Number(point.lng),
          localElevationRaw: localElevationRaw
        })
      }
      return
    }
    const rawValues = await localTerrainState.sampleElevationsRaw(points, { resampling: 'nearest' })
    for (let idx = 0; idx < points.length && samples.length < requested; idx += 1) {
      const localElevationRaw = Number(rawValues[idx])
      if (!Number.isFinite(localElevationRaw)) continue
      samples.push({
        lat: Number(points[idx].lat),
        lng: Number(points[idx].lng),
        localElevationRaw: localElevationRaw
      })
    }
  }

  const gridSize = Math.max(10, Math.ceil(Math.sqrt(requested * 1.8)))
  const gridCandidates = []
  for (let row = 0; row < gridSize && samples.length < requested; row++) {
    for (let col = 0; col < gridSize && samples.length < requested; col++) {
      const lat = minLat + (((row + 0.5) / gridSize) * (maxLat - minLat))
      const lng = minLng + (((col + 0.5) / gridSize) * (maxLng - minLng))
      if (!fnContainsLatLng(lat, lng)) continue
      gridCandidates.push({ lat: lat, lng: lng })
    }
  }
  const sampleBatchSize = Math.max(20, Math.min(200, Math.trunc(Number(TERRAIN_CALIBRATION_PROVIDER_BATCH_SIZE) || 120)))
  for (let from = 0; from < gridCandidates.length && samples.length < requested; from += sampleBatchSize) {
    await fnAppendBatchSamples(gridCandidates.slice(from, from + sampleBatchSize))
  }

  if (samples.length < requested) {
    let seed = 2463534242
    const fnRand = function () {
      seed = ((seed * 1664525) + 1013904223) >>> 0
      return seed / 4294967296
    }
    const maxAttempts = Math.max(requested * 14, 400)
    let attempts = 0
    let randomBatch = []
    while (samples.length < requested && attempts < maxAttempts) {
      attempts += 1
      const lat = minLat + (fnRand() * (maxLat - minLat))
      const lng = minLng + (fnRand() * (maxLng - minLng))
      if (!fnContainsLatLng(lat, lng)) continue
      randomBatch.push({ lat: lat, lng: lng })
      if (randomBatch.length >= sampleBatchSize) {
        await fnAppendBatchSamples(randomBatch)
        randomBatch = []
      }
    }
    if (randomBatch.length && samples.length < requested) {
      await fnAppendBatchSamples(randomBatch)
    }
  }

  return samples.slice(0, requested)
}

const fnFetchProviderElevationsFromApi = async function (providerKey, points) {
  if (typeof fetch !== 'function') return null
  const payload = {
    provider: String(providerKey || '').trim(),
    points: (Array.isArray(points) ? points : []).map((pt) => ({
      lat: Number(pt && pt.lat),
      lng: Number(pt && pt.lng)
    }))
  }
  const response = await fetch('/api/dem-elevation', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  })
  const body = await response.json().catch(function () { return null })
  if (!response.ok) {
    const errMessage = String((body && body.error) || ('DEM API HTTP ' + response.status))
    throw new Error(errMessage)
  }
  const results = Array.isArray(body && body.results) ? body.results : []
  return results.map((row) => {
    const rawElevation = row && Object.prototype.hasOwnProperty.call(row, 'elevation') ? row.elevation : null
    if (rawElevation === null || rawElevation === undefined) return null
    if (typeof rawElevation === 'string' && !String(rawElevation).trim()) return null
    const elevation = Number(rawElevation)
    return Number.isFinite(elevation) ? elevation : null
  })
}

const fnApplyProviderPriorityToCalibrationSamples = async function (samples, providerOrder) {
  const list = Array.isArray(samples) ? samples : []
  const order = Array.isArray(providerOrder) ? providerOrder : []
  const providerStats = []
  const batchSize = Math.max(10, Math.min(500, Math.trunc(Number(TERRAIN_CALIBRATION_PROVIDER_BATCH_SIZE) || 120)))
  for (let i = 0; i < order.length; i++) {
    const providerKey = String(order[i] || '').trim()
    if (!providerKey) continue
    const unresolvedIndexes = []
    for (let idx = 0; idx < list.length; idx++) {
      if (Number.isFinite(Number(list[idx] && list[idx].providerElevation))) continue
      unresolvedIndexes.push(idx)
    }
    if (!unresolvedIndexes.length) break
    const points = unresolvedIndexes.map((idx) => ({
      lat: Number(list[idx].lat),
      lng: Number(list[idx].lng)
    }))
    const stat = {
      provider: providerKey,
      requested: unresolvedIndexes.length,
      resolved: 0,
      failed: unresolvedIndexes.length,
      chunks: 0,
      ok: false,
      error: ''
    }
    const errors = []
    for (let from = 0; from < unresolvedIndexes.length; from += batchSize) {
      const to = Math.min(unresolvedIndexes.length, from + batchSize)
      const chunkIndexes = unresolvedIndexes.slice(from, to)
      const chunkPoints = points.slice(from, to)
      stat.chunks += 1
      try {
        const elevations = await fnFetchProviderElevationsFromApi(providerKey, chunkPoints)
        if (!Array.isArray(elevations)) continue
        for (let localIdx = 0; localIdx < elevations.length; localIdx++) {
          const idx = chunkIndexes[localIdx]
          const elevation = Number(elevations[localIdx])
          if (!Number.isFinite(elevation)) continue
          list[idx].providerElevation = elevation
          list[idx].providerKey = providerKey
          stat.resolved += 1
        }
      } catch (error) {
        errors.push(String((error && error.message) || error || 'chunk_failed'))
      }
    }
    stat.failed = stat.requested - stat.resolved
    stat.ok = stat.resolved > 0
    stat.error = errors.join(' | ')
    providerStats.push(stat)
  }
  return {
    samples: list,
    providerStats: providerStats
  }
}

const fnComputeTerrainCalibrationShift = function (deltas, options) {
  const opts = Object.assign({
    maxAbsDeltaMeters: 150,
    binSizeMeters: 0.5
  }, options || {})
  const valid = (Array.isArray(deltas) ? deltas : [])
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && Math.abs(v) <= opts.maxAbsDeltaMeters)
  if (!valid.length) return null

  const binSize = Math.max(0.1, Number(opts.binSizeMeters) || 0.5)
  const bins = new Map()
  valid.forEach((delta) => {
    const binKey = Math.round(delta / binSize)
    bins.set(binKey, Number(bins.get(binKey) || 0) + 1)
  })
  let dominantBinKey = null
  let dominantBinCount = 0
  bins.forEach((count, binKey) => {
    if (count > dominantBinCount) {
      dominantBinCount = count
      dominantBinKey = binKey
    }
  })

  const dominantCenter = (dominantBinKey === null) ? 0 : (Number(dominantBinKey) * binSize)
  const dominantCluster = valid.filter((delta) => Math.abs(delta - dominantCenter) <= (binSize * 2))
  const minDominantCount = Math.max(8, Math.round(valid.length * 0.35))
  const baseSet = dominantCluster.length >= minDominantCount ? dominantCluster : valid
  const medianDelta = fnMedian(baseSet)
  if (!Number.isFinite(Number(medianDelta))) return null
  const mad = fnMedian(baseSet.map((delta) => Math.abs(delta - medianDelta))) || 0
  const inlierThreshold = Math.max(binSize * 2, mad * 3)
  const inlierCount = valid.filter((delta) => Math.abs(delta - medianDelta) <= inlierThreshold).length
  const inlierPct = valid.length ? (inlierCount / valid.length) : 0
  let quality = 'poor'
  if (inlierPct >= 0.7 && mad <= 2.5) quality = 'good'
  else if (inlierPct >= 0.45 && mad <= 6) quality = 'warning'

  return {
    shiftMeters: Math.round(Number(medianDelta) * 10) / 10,
    medianDeltaMeters: Number(medianDelta),
    madMeters: Math.round(Number(mad) * 100) / 100,
    inlierThresholdMeters: Math.round(Number(inlierThreshold) * 100) / 100,
    inlierCount: inlierCount,
    inlierPct: Math.round(inlierPct * 1000) / 10,
    validCount: valid.length,
    dominantBinCenterMeters: dominantCenter,
    dominantBinCount: dominantBinCount,
    quality: quality
  }
}

const fnCalibrateTerrainShiftByProviders = async function (localTerrainState, options) {
  const opts = Object.assign({
    sampleCount: TERRAIN_CALIBRATION_SAMPLE_COUNT
  }, options || {})
  if (localTerrainState && localTerrainState.verticalReferenceDetected === true) {
    return {
      applied: false,
      reason: 'vertical_reference_detected',
      shiftMeters: 0,
      sampleCountRequested: opts.sampleCount,
      sampleCountBuilt: 0,
      sampleCountMatched: 0
    }
  }
  const samples = await fnBuildTerrainCalibrationSamples(localTerrainState, opts.sampleCount)
  if (!samples.length) {
    return {
      applied: false,
      reason: 'no_valid_sample_points',
      shiftMeters: 0,
      sampleCountRequested: opts.sampleCount
    }
  }
  const providerOrder = fnBuildTerrainCalibrationProviderOrder()
  if (!providerOrder.length) {
    return {
      applied: false,
      reason: 'no_provider_available',
      shiftMeters: 0,
      sampleCountRequested: opts.sampleCount,
      sampleCountBuilt: samples.length
    }
  }
  const providerResolved = await fnApplyProviderPriorityToCalibrationSamples(samples, providerOrder)
  const resolvedSamples = providerResolved.samples || []
  const matchedSamples = resolvedSamples.filter((row) => {
    return Number.isFinite(Number(row && row.localElevationRaw)) && Number.isFinite(Number(row && row.providerElevation))
  })
  const sampleRowsAll = resolvedSamples.map((row, idx) => {
    const localRaw = Number(row && row.localElevationRaw)
    const providerElevation = Number(row && row.providerElevation)
    const hasLocal = Number.isFinite(localRaw)
    const hasProvider = Number.isFinite(providerElevation)
    const delta = (hasLocal && hasProvider) ? (localRaw - providerElevation) : null
    return {
      sampleIndex: idx + 1,
      lat: Number(row && row.lat),
      lng: Number(row && row.lng),
      localRaw: hasLocal ? (Math.round(localRaw * 10) / 10) : null,
      provider: hasProvider ? (Math.round(providerElevation * 10) / 10) : null,
      shiftDeltaMeters: Number.isFinite(delta) ? (Math.round(delta * 10) / 10) : null,
      providerKey: String(row && row.providerKey || ''),
      matched: !!(hasLocal && hasProvider)
    }
  })
  if (!matchedSamples.length) {
    return {
      applied: false,
      reason: 'providers_returned_no_elevation',
      shiftMeters: 0,
      sampleCountRequested: opts.sampleCount,
      sampleCountBuilt: samples.length,
      sampleCountMatched: 0,
      providerOrder: providerOrder,
      providerStats: providerResolved.providerStats || [],
      sampleRowsAll: sampleRowsAll
    }
  }
  const deltas = matchedSamples.map((row) => Number(row.localElevationRaw) - Number(row.providerElevation))
  const robust = fnComputeTerrainCalibrationShift(deltas, {
    maxAbsDeltaMeters: 150,
    binSizeMeters: 0.5
  })
  if (!robust) {
    return {
      applied: false,
      reason: 'robust_shift_failed',
      shiftMeters: 0,
      sampleCountRequested: opts.sampleCount,
      sampleCountBuilt: samples.length,
      sampleCountMatched: matchedSamples.length,
      providerOrder: providerOrder,
      providerStats: providerResolved.providerStats || [],
      sampleRowsAll: sampleRowsAll
    }
  }
  return {
    applied: true,
    reason: 'ok',
    shiftMeters: Number(robust.shiftMeters),
    sampleCountRequested: opts.sampleCount,
    sampleCountBuilt: samples.length,
    sampleCountMatched: matchedSamples.length,
    providerOrder: providerOrder,
    providerStats: providerResolved.providerStats || [],
    robust: robust,
    sampleRowsAll: sampleRowsAll
  }
}

const fnValidateCachedTerrainMeta = async function (localTerrainState, terrainMeta, options) {
  const opts = Object.assign({
    sampleCount: 80,
    maxShiftDeltaMeters: 3
  }, options || {})
  const cachedShift = Math.round(Number(terrainMeta && terrainMeta.verticalShiftMeters || 0) * 10) / 10
  if (!localTerrainState || typeof localTerrainState !== 'object') {
    return { reusable: false, reason: 'missing_local_terrain_state', calibrationInfo: null, validationInfo: null }
  }
  if (!Number.isFinite(cachedShift)) {
    return { reusable: false, reason: 'cached_shift_invalid', calibrationInfo: null, validationInfo: null }
  }
  if (localTerrainState.verticalReferenceDetected === true) {
    if (Math.abs(cachedShift) <= 0.1) {
      return {
        reusable: true,
        reason: 'explicit_vertical_reference_zero_shift',
        calibrationInfo: null,
        validationInfo: null
      }
    }
    return {
      reusable: false,
      reason: 'explicit_vertical_reference_nonzero_cached_shift',
      calibrationInfo: {
        applied: false,
        reason: 'vertical_reference_detected',
        shiftMeters: 0,
        sampleCountRequested: opts.sampleCount,
        sampleCountBuilt: 0,
        sampleCountMatched: 0
      },
      validationInfo: null
    }
  }
  let validationInfo = null
  try {
    validationInfo = await fnCalibrateTerrainShiftByProviders(localTerrainState, {
      sampleCount: opts.sampleCount
    })
  } catch (_error) {
    return {
      reusable: true,
      reason: 'validation_error_keep_cache',
      calibrationInfo: null,
      validationInfo: null
    }
  }
  if (!validationInfo || validationInfo.applied !== true) {
    return {
      reusable: true,
      reason: String((validationInfo && validationInfo.reason) || 'validation_skipped_keep_cache'),
      calibrationInfo: null,
      validationInfo: validationInfo || null
    }
  }
  const validatedShift = Math.round(Number(validationInfo.shiftMeters || 0) * 10) / 10
  const shiftDelta = Math.abs(validatedShift - cachedShift)
  if (shiftDelta <= Math.max(0.5, Number(opts.maxShiftDeltaMeters) || 3)) {
    return {
      reusable: true,
      reason: 'validated_keep_cache',
      calibrationInfo: null,
      validationInfo: validationInfo
    }
  }
  return {
    reusable: false,
    reason: 'cached_shift_mismatch',
    calibrationInfo: Object.assign({}, validationInfo, {
      reason: 'validation_recalculated',
      shiftMeters: validatedShift
    }),
    validationInfo: validationInfo
  }
}

const fnBuildLocalTerrainState = function (georaster, file, signature, options) {
  const opts = (options && typeof options === 'object') ? options : {}
  const verticalReferenceInfo = (opts.verticalReferenceInfo && typeof opts.verticalReferenceInfo === 'object')
    ? opts.verticalReferenceInfo
    : {
        detected: false,
        key: TIFF_VERTICAL_REFERENCE_NONE,
        label: fnGetTiffVerticalReferenceLabel(TIFF_VERTICAL_REFERENCE_NONE),
        source: 'none',
        raw: null
      }
  const verticalReference = fnNormalizeTiffVerticalReference(verticalReferenceInfo.key, TIFF_VERTICAL_REFERENCE_NONE)
  const verticalReferenceLabel = String(verticalReferenceInfo.label || fnGetTiffVerticalReferenceLabel(verticalReference))
  let verticalShiftMeters = fnToFiniteNumber(opts.verticalShiftMeters)
  if (verticalShiftMeters === null) verticalShiftMeters = 0
  const width = Math.max(1, Number(georaster && georaster.width) || 0)
  const height = Math.max(1, Number(georaster && georaster.height) || 0)
  if (!width || !height) return null
  const values = georaster && georaster.values
  if (!Array.isArray(values) || !values.length) return null
  const coordTransform = fnBuildGeoCoordTransform(georaster)

  const west = Math.min(Number(georaster.xmin), Number(georaster.xmax))
  const east = Math.max(Number(georaster.xmin), Number(georaster.xmax))
  const south = Math.min(Number(georaster.ymin), Number(georaster.ymax))
  const north = Math.max(Number(georaster.ymin), Number(georaster.ymax))
  if (!Number.isFinite(west) || !Number.isFinite(east) || !Number.isFinite(south) || !Number.isFinite(north)) return null
  if (!(east > west) || !(north > south)) return null

  const bandStats = values.map((_band, idx) => fnBuildBandStats(georaster, idx))
  let heightBandIndex = 0
  let bestScore = -Infinity
  bandStats.forEach((stats, idx) => {
    const finiteCount = Number(stats && stats.finiteCount) || 0
    if (finiteCount <= 0) return
    const uniqueCount = Number(stats && stats.uniqueCount) || 0
    const range = Number(stats && stats.range) || 0
    const binaryPenalty = (stats && stats.binaryLike) ? 1000 : 0
    const score = (range * 10) + uniqueCount - binaryPenalty
    if (score > bestScore) {
      bestScore = score
      heightBandIndex = idx
    }
  })

  let maskBandIndex = -1
  for (let i = 0; i < bandStats.length; i++) {
    if (i === heightBandIndex) continue
    const stats = bandStats[i]
    if (!stats || !stats.binaryLike) continue
    if ((Number(stats.finiteCount) || 0) <= 0) continue
    maskBandIndex = i
    break
  }

  const noDataValue = fnToFiniteNumber(georaster.noDataValue)
  const pixelWidth = (east - west) / width
  const pixelHeight = (north - south) / height
  const heightBand = values[heightBandIndex]
  const maskBand = (maskBandIndex >= 0) ? values[maskBandIndex] : null

  const fnLatLngToCell = function (lat, lng) {
    const latNum = Number(lat)
    const lngNum = Number(lng)
    if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return null
    const source = coordTransform.latLngToSource(latNum, lngNum)
    if (!source) return null
    if (source.x < west || source.x > east || source.y < south || source.y > north) return null
    const col = Math.max(0, Math.min(width - 1, Math.floor((source.x - west) / pixelWidth)))
    const row = Math.max(0, Math.min(height - 1, Math.floor((north - source.y) / pixelHeight)))
    return { row, col }
  }

  const fnCellToLatLng = function (row, col) {
    const sourceY = north - ((Number(row) + 0.5) * pixelHeight)
    const sourceX = west + ((Number(col) + 0.5) * pixelWidth)
    return coordTransform.sourceToLatLng(sourceX, sourceY)
  }

  const fnIsCellCovered = function (row, col) {
    const h = fnGetBandValueAt(heightBand, row, col, width, height)
    if (h === null) return false
    if (noDataValue !== null && Math.abs(h - noDataValue) < 1e-7) return false
    if (maskBand) {
      const m = fnGetBandValueAt(maskBand, row, col, width, height)
      if (m === null) return false
      if (!(m > 0)) return false
    }
    return true
  }

  const fnGetVerticalShiftMeters = function () {
    const n = fnToFiniteNumber(verticalShiftMeters)
    return (n === null) ? 0 : n
  }

  const fnSetVerticalShiftMeters = function (value) {
    const n = fnToFiniteNumber(value)
    verticalShiftMeters = (n === null) ? 0 : n
    return verticalShiftMeters
  }

  const fnSampleElevationRaw = function (lat, lng) {
    if (!fnHasCoverageAtLatLng(lat, lng)) return null
    const cell = fnLatLngToCell(lat, lng)
    if (!cell) return null
    if (!fnIsCellCovered(cell.row, cell.col)) return null
    const h = fnGetBandValueAt(heightBand, cell.row, cell.col, width, height)
    if (h === null) return null
    return Math.round(h * 10) / 10
  }

  const fnSampleElevation = function (lat, lng) {
    const raw = fnSampleElevationRaw(lat, lng)
    if (!Number.isFinite(Number(raw))) return null
    const shifted = Number(raw) - fnGetVerticalShiftMeters()
    return Math.round(shifted * 10) / 10
  }

  const sampleStepRow = Math.max(1, Math.floor(height / 96))
  const sampleStepCol = Math.max(1, Math.floor(width / 96))
  const coveragePoints = []
  for (let row = 0; row < height; row += sampleStepRow) {
    for (let col = 0; col < width; col += sampleStepCol) {
      if (!fnIsCellCovered(row, col)) continue
      const latLng = fnCellToLatLng(row, col)
      if (!latLng || !fnIsLatLngInRange(latLng.lat, latLng.lng)) continue
      coveragePoints.push({ x: latLng.lng, y: latLng.lat })
    }
  }
  const hull = fnBuildConvexHull(coveragePoints)
  let rawFootprintLatLngs = hull.map((pt) => [pt.y, pt.x])
  if (rawFootprintLatLngs.length < 3) {
    const sw = coordTransform.sourceToLatLng(west, south)
    const se = coordTransform.sourceToLatLng(east, south)
    const ne = coordTransform.sourceToLatLng(east, north)
    const nw = coordTransform.sourceToLatLng(west, north)
    const rect = [sw, se, ne, nw].filter((pt) => !!pt && fnIsLatLngInRange(pt.lat, pt.lng))
    if (rect.length >= 3) {
      rawFootprintLatLngs = rect.map((pt) => [pt.lat, pt.lng])
    } else {
      rawFootprintLatLngs = []
    }
  }
  const footprintLatLngs = fnBuildEffectiveTerrainCoverageFootprint(rawFootprintLatLngs)
  const fnHasCoverageAtLatLng = function (lat, lng) {
    const latNum = Number(lat)
    const lngNum = Number(lng)
    if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return false
    if (footprintLatLngs.length >= 3) {
      return fnIsPointInsideLatLngPolygon(latNum, lngNum, footprintLatLngs)
    }
    return !!fnLatLngToCell(latNum, lngNum)
  }

  return {
    active: true,
    sourceName: String(file && file.name || ''),
    signature: signature || null,
    width,
    height,
    bounds: { south, west, north, east },
    projectionEpsg: coordTransform.epsg,
    projectionIsGeographic: coordTransform.projectionIsGeographic,
    projectionTransformAvailable: coordTransform.projectionTransformAvailable,
    projectionHasProj4: coordTransform.hasProj4,
    projectionSourceCrsDefined: coordTransform.sourceCrsDefined,
    projectionSourceCrsAutoDefined: coordTransform.sourceCrsAutoDefined,
    verticalReference: verticalReference,
    verticalReferenceLabel: verticalReferenceLabel,
    verticalReferenceDetected: !!(verticalReferenceInfo.detected === true),
    verticalReferenceSource: String(verticalReferenceInfo.source || 'none'),
    verticalReferenceRaw: verticalReferenceInfo.raw === undefined ? null : verticalReferenceInfo.raw,
    verticalShiftMeters: fnGetVerticalShiftMeters(),
    hasCoverage: footprintLatLngs.length >= 3,
    heightBandIndex,
    maskBandIndex,
    rawFootprintLatLngs,
    footprintLatLngs,
    sampleElevationRaw: fnSampleElevationRaw,
    sampleElevation: fnSampleElevation,
    getVerticalShiftMeters: fnGetVerticalShiftMeters,
    setVerticalShiftMeters: fnSetVerticalShiftMeters,
    containsLatLng: fnHasCoverageAtLatLng
  }
}

const fnNormalizeLatLngFootprint = function (latLngs) {
  const rows = (Array.isArray(latLngs) ? latLngs : []).map(function (row) {
    const lat = Number(Array.isArray(row) ? row[0] : null)
    const lng = Number(Array.isArray(row) ? row[1] : null)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
    return [lat, lng]
  }).filter(function (row) {
    return !!row
  })
  return fnAreLatLngPolygonSane(rows) ? rows : []
}

const fnBuildInsetLatLngFootprint = function (latLngs, insetMeters) {
  const footprint = fnNormalizeLatLngFootprint(latLngs)
  const inset = fnToFiniteNumber(insetMeters)
  if (!footprint.length || inset === null || !(inset > 0)) return footprint
  const centroid = footprint.reduce(function (acc, row) {
    return {
      lat: acc.lat + Number(row[0]),
      lng: acc.lng + Number(row[1])
    }
  }, { lat: 0, lng: 0 })
  centroid.lat /= footprint.length
  centroid.lng /= footprint.length
  if (!fnIsLatLngInRange(centroid.lat, centroid.lng)) return footprint
  const insetFootprint = footprint.map(function (row) {
    const lat = Number(row[0])
    const lng = Number(row[1])
    const bearingToCentroid = bearingBetweenLatLng(lat, lng, centroid.lat, centroid.lng)
    const shifted = offsetLatLngByMeters(lat, lng, inset, bearingToCentroid)
    if (!shifted || !fnIsLatLngInRange(shifted.lat, shifted.lng)) return null
    return [shifted.lat, shifted.lng]
  }).filter(function (row) {
    return !!row
  })
  const insetMetrics = fnGetLatLngPolygonMetrics(insetFootprint)
  if (!insetMetrics.sane || !(insetMetrics.latSpan > 0) || !(insetMetrics.lngSpan > 0)) {
    return footprint
  }
  return insetFootprint
}

const fnBuildEffectiveTerrainCoverageFootprint = function (latLngs) {
  return fnBuildInsetLatLngFootprint(latLngs, LOCAL_TERRAIN_COVERAGE_INSET_METERS)
}

const fnSetLocalTerrainState = function (nextState) {
  if (typeof window === 'undefined') return
  window[PORTAL_LOCAL_TERRAIN_STATE_KEY] = nextState || null
}

const fnResolveGeoTiffModeDecision = function (requestedMode, georaster, localTerrainState, file) {
  const mode = String(requestedMode || 'auto').trim().toLowerCase()
  if (mode === 'ortho') return { mode: 'ortho', reason: 'explicit_ortho' }
  if (mode === 'terrain') return { mode: 'terrain', reason: 'explicit_terrain' }

  const fileName = String(file && file.name || '').trim().toLowerCase()
  if (/(^|[^a-z])(dem|dsm|dtm|elevation|height)([^a-z]|$)/.test(fileName)) return { mode: 'terrain', reason: 'filename_dem_dsm_keyword' }
  if (/(^|[^a-z])(ortho|rgb|imagery|mosaic)([^a-z]|$)/.test(fileName)) return { mode: 'ortho', reason: 'filename_ortho_keyword' }

  const values = georaster && Array.isArray(georaster.values) ? georaster.values : []
  const bandCount = values.length
  if (localTerrainState && localTerrainState.maskBandIndex >= 0) return { mode: 'terrain', reason: 'mask_band_detected' }
  if (bandCount <= 1) return { mode: 'terrain', reason: 'single_band_raster' }
  if (bandCount === 2 && localTerrainState && localTerrainState.hasCoverage) return { mode: 'terrain', reason: 'two_band_with_coverage' }
  return { mode: 'ortho', reason: 'fallback_multiband_visual' }
}

const fnLooksLikeAbsoluteFilePath = function (rawPath) {
  const value = String(rawPath || '').trim()
  if (!value) return false
  if (/^[a-zA-Z]:[\\/]/.test(value)) return true
  if (value.startsWith('\\\\')) return true
  if (value.startsWith('/')) return true
  return false
}

const fnIsElectronRuntime = function () {
  return !!(typeof window !== 'undefined' && window.electronAPI && window.electronAPI.isElectron === true)
}

const fnFetchGeoTiffSampleBatch = async function ({
  tileSetId,
  points,
  resampling = 'nearest'
} = {}) {
  const resolvedTileSetId = String(tileSetId || '').trim()
  const rows = Array.isArray(points) ? points : []
  if (!resolvedTileSetId || !rows.length) return []
  if (typeof fetch !== 'function') return []
  const response = await fetch('/api/geotiff-tiles/sample', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tileSetId: resolvedTileSetId,
      resampling: String(resampling || 'nearest').trim() || 'nearest',
      points: rows.map((point) => ({
        lat: Number(point && point.lat),
        lng: Number(point && point.lng)
      }))
    })
  })
  const payload = await response.json().catch(function () { return null })
  if (!response.ok) {
    const reason = String((payload && payload.error) || ('HTTP ' + String(response.status))).trim()
    throw new Error(reason || 'sample_failed')
  }
  const sample = (payload && payload.sample && typeof payload.sample === 'object') ? payload.sample : {}
  const results = Array.isArray(sample.results) ? sample.results : []
  return rows.map(function (_point, idx) {
    const row = results[idx]
    const rawElevation = row && Object.prototype.hasOwnProperty.call(row, 'elevation') ? row.elevation : null
    if (rawElevation === null || rawElevation === undefined) return null
    if (typeof rawElevation === 'string' && !String(rawElevation).trim()) return null
    const elevation = Number(rawElevation)
    return Number.isFinite(elevation) ? (Math.round(elevation * 10) / 10) : null
  })
}

const fnBuildFootprintFromBounds = function (bounds) {
  const src = (bounds && typeof bounds === 'object') ? bounds : null
  if (
    !src ||
    ![src.minX, src.minY, src.maxX, src.maxY].every(function (value) { return Number.isFinite(Number(value)) })
  ) {
    return []
  }
  return [
    [Number(src.maxY), Number(src.minX)],
    [Number(src.minY), Number(src.minX)],
    [Number(src.minY), Number(src.maxX)],
    [Number(src.maxY), Number(src.maxX)]
  ]
}

const fnNormalizeServerTerrainFootprint = function (tileSet) {
  const sourceInfo = (tileSet && tileSet.sourceInfo && typeof tileSet.sourceInfo === 'object') ? tileSet.sourceInfo : {}
  const rawFootprint = Array.isArray(sourceInfo.footprintLatLngs) ? sourceInfo.footprintLatLngs : []
  const footprint = rawFootprint
    .map(function (coord) {
      const lat = Number(Array.isArray(coord) ? coord[0] : null)
      const lng = Number(Array.isArray(coord) ? coord[1] : null)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
      return [lat, lng]
    })
    .filter(function (coord) { return !!coord })
  if (footprint.length >= 3) return footprint
  const fallbackBounds = (sourceInfo.bounds && typeof sourceInfo.bounds === 'object')
    ? sourceInfo.bounds
    : ((tileSet && tileSet.bounds && typeof tileSet.bounds === 'object') ? tileSet.bounds : null)
  return fnBuildFootprintFromBounds(fallbackBounds)
}

const fnBuildFootprintBoundsFromTileSet = function (tileSet) {
  const footprint = fnNormalizeServerTerrainFootprint(tileSet)
  if (!Array.isArray(footprint) || footprint.length < 3) return null
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  footprint.forEach(function (coord) {
    const lat = Number(Array.isArray(coord) ? coord[0] : null)
    const lng = Number(Array.isArray(coord) ? coord[1] : null)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
    minX = Math.min(minX, lng)
    minY = Math.min(minY, lat)
    maxX = Math.max(maxX, lng)
    maxY = Math.max(maxY, lat)
  })
  if (![minX, minY, maxX, maxY].every(function (value) { return Number.isFinite(value) })) return null
  return {
    minX: minX,
    minY: minY,
    maxX: maxX,
    maxY: maxY
  }
}

const fnBuildLeafletBoundsFromTileSet = function (tileSet) {
  const sourceInfo = (tileSet && tileSet.sourceInfo && typeof tileSet.sourceInfo === 'object') ? tileSet.sourceInfo : {}
  const footprintBounds = fnBuildFootprintBoundsFromTileSet(tileSet)
  if (
    footprintBounds &&
    [footprintBounds.minX, footprintBounds.minY, footprintBounds.maxX, footprintBounds.maxY].every(function (value) { return Number.isFinite(Number(value)) }) &&
    window.L &&
    typeof window.L.latLngBounds === 'function'
  ) {
    return window.L.latLngBounds(
      [Number(footprintBounds.minY), Number(footprintBounds.minX)],
      [Number(footprintBounds.maxY), Number(footprintBounds.maxX)]
    )
  }
  const bounds = (sourceInfo.bounds && typeof sourceInfo.bounds === 'object')
    ? sourceInfo.bounds
    : ((tileSet && tileSet.bounds && typeof tileSet.bounds === 'object') ? tileSet.bounds : null)
  if (
    !bounds ||
    !(window.L && typeof window.L.latLngBounds === 'function') ||
    ![bounds.minY, bounds.minX, bounds.maxY, bounds.maxX].every(function (value) { return Number.isFinite(Number(value)) })
  ) {
    return null
  }
  return window.L.latLngBounds(
    [Number(bounds.minY), Number(bounds.minX)],
    [Number(bounds.maxY), Number(bounds.maxX)]
  )
}

const fnBuildServerTerrainState = function (tileSet, file, signature, options) {
  const opts = (options && typeof options === 'object') ? options : {}
  const sourceInfo = (tileSet && tileSet.sourceInfo && typeof tileSet.sourceInfo === 'object') ? tileSet.sourceInfo : {}
  const rawFootprintLatLngs = fnNormalizeServerTerrainFootprint(tileSet)
  const footprintLatLngs = fnBuildEffectiveTerrainCoverageFootprint(rawFootprintLatLngs)
  const boundsSrc = (sourceInfo.bounds && typeof sourceInfo.bounds === 'object')
    ? sourceInfo.bounds
    : ((tileSet && tileSet.bounds && typeof tileSet.bounds === 'object') ? tileSet.bounds : null)
  const bounds = boundsSrc
    ? {
        south: Number(boundsSrc.minY),
        west: Number(boundsSrc.minX),
        north: Number(boundsSrc.maxY),
        east: Number(boundsSrc.maxX)
      }
    : { south: 0, west: 0, north: 0, east: 0 }
  let verticalShiftMeters = fnToFiniteNumber(opts.verticalShiftMeters)
  if (verticalShiftMeters === null) verticalShiftMeters = 0
  const state = {
    active: true,
    serverBacked: true,
    sourceName: String(file && file.name || ''),
    signature: signature || null,
    tileSetId: String(tileSet && tileSet.tileSetId || ''),
    width: Math.max(0, Number(sourceInfo.width || 0)),
    height: Math.max(0, Number(sourceInfo.height || 0)),
    bounds: bounds,
    projectionEpsg: Number.isFinite(Number(sourceInfo.projectionEpsg)) ? Number(sourceInfo.projectionEpsg) : null,
    projectionIsGeographic: sourceInfo.projectionIsGeographic === true,
    projectionTransformAvailable: sourceInfo.projectionTransformAvailable !== false,
    projectionHasProj4: sourceInfo.projectionHasProj4 !== false,
    projectionSourceCrsDefined: sourceInfo.projectionSourceCrsDefined === true,
    projectionSourceCrsAutoDefined: sourceInfo.projectionSourceCrsAutoDefined === true,
    verticalReference: fnNormalizeTiffVerticalReference(sourceInfo.verticalReference, TIFF_VERTICAL_REFERENCE_NONE),
    verticalReferenceLabel: String(sourceInfo.verticalReferenceLabel || fnGetTiffVerticalReferenceLabel(sourceInfo.verticalReference)),
    verticalReferenceDetected: sourceInfo.verticalReferenceDetected === true,
    verticalReferenceSource: String(sourceInfo.verticalReferenceSource || 'server'),
    verticalShiftMeters: verticalShiftMeters,
    hasCoverage: footprintLatLngs.length >= 3,
    heightBandIndex: Math.max(0, Number(sourceInfo.heightBandIndex || 0)),
    maskBandIndex: Number.isFinite(Number(sourceInfo.maskBandIndex)) ? Number(sourceInfo.maskBandIndex) : -1,
    rawFootprintLatLngs: rawFootprintLatLngs,
    footprintLatLngs: footprintLatLngs
  }
  state.getVerticalShiftMeters = function () {
    const n = fnToFiniteNumber(state.verticalShiftMeters)
    return n === null ? 0 : n
  }
  state.setVerticalShiftMeters = function (value) {
    const n = fnToFiniteNumber(value)
    state.verticalShiftMeters = (n === null) ? 0 : n
    return state.verticalShiftMeters
  }
  state.containsLatLng = function (lat, lng) {
    const latNum = Number(lat)
    const lngNum = Number(lng)
    if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return false
    const footprint = Array.isArray(state.footprintLatLngs) ? state.footprintLatLngs : []
    if (footprint.length >= 3) {
      return fnIsPointInsideLatLngPolygon(latNum, lngNum, footprint)
    }
    return (
      Number.isFinite(Number(bounds.south)) &&
      Number.isFinite(Number(bounds.west)) &&
      Number.isFinite(Number(bounds.north)) &&
      Number.isFinite(Number(bounds.east)) &&
      latNum >= Number(bounds.south) &&
      latNum <= Number(bounds.north) &&
      lngNum >= Number(bounds.west) &&
      lngNum <= Number(bounds.east)
    )
  }
  state.sampleElevationsRaw = async function (points, sampleOptions) {
    const sampleOpts = (sampleOptions && typeof sampleOptions === 'object') ? sampleOptions : {}
    const normalizedPoints = (Array.isArray(points) ? points : []).map(function (point) {
      return {
        lat: Number(point && point.lat),
        lng: Number(point && point.lng)
      }
    })
    const sampleIndexMap = []
    const requestPoints = []
    normalizedPoints.forEach(function (point, idx) {
      if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) return
      if (state.containsLatLng(point.lat, point.lng) !== true) return
      sampleIndexMap.push(idx)
      requestPoints.push(point)
    })
    const out = normalizedPoints.map(function () { return null })
    if (!requestPoints.length) return out
    const sampledValues = await fnFetchGeoTiffSampleBatch({
      tileSetId: state.tileSetId,
      points: requestPoints,
      resampling: sampleOpts.resampling || 'nearest'
    })
    sampleIndexMap.forEach(function (originalIdx, sampleIdx) {
      out[originalIdx] = sampleIdx < sampledValues.length ? sampledValues[sampleIdx] : null
    })
    return out
  }
  state.sampleElevations = async function (points, sampleOptions) {
    const rawValues = await state.sampleElevationsRaw(points, sampleOptions)
    return rawValues.map(function (value) {
      const raw = fnToFiniteNumber(value)
      if (raw === null) return null
      return Math.round((raw - state.getVerticalShiftMeters()) * 10) / 10
    })
  }
  state.sampleElevationRawAsync = async function (lat, lng, sampleOptions) {
    const values = await state.sampleElevationsRaw([{ lat: lat, lng: lng }], sampleOptions)
    const raw = values.length ? values[0] : null
    return fnToFiniteNumber(raw)
  }
  state.sampleElevationAsync = async function (lat, lng, sampleOptions) {
    const values = await state.sampleElevations([{ lat: lat, lng: lng }], sampleOptions)
    const value = values.length ? values[0] : null
    return fnToFiniteNumber(value)
  }
  return state
}

const fnTryLoadFromServerTiles = async function ({
  sourcePath,
  map,
  sourceLayerGroup,
  fnSetTiffLayer,
  fnSetTiffLastFile,
  fnClearGeoTiffLayer,
  file,
  fileSignature,
  sourceType,
  db,
  Swal,
  fnT,
  fnTF,
  fnGeoTiffLog,
  loadingTask,
  autoAlignTerrain,
  geoTiffCacheOptions
}) {
  const resolvedPath = String(sourcePath || '').trim()
  if (!resolvedPath) return false
  if (!fnLooksLikeAbsoluteFilePath(resolvedPath)) return false
  if (!fnIsElectronRuntime()) return false
  if (typeof fetch !== 'function') return false
  if (!(window.L && typeof window.L.tileLayer === 'function')) return false

  const prepareOptions = (sourceType === 'terrain')
    ? { minZoom: 12, maxZoom: 19, resampling: 'bilinear' }
    : { minZoom: 16, maxZoom: 22, resampling: 'bilinear' }

  const prepareStartedAt = Date.now()
  let prepareTimer = null
  const fnUpdatePrepareStatus = function () {
    const elapsedSeconds = Math.max(0, Math.floor((Date.now() - prepareStartedAt) / 1000))
    loadingTask.update({
      progress: null,
      mode: 'indeterminate',
      status: fnTF('geotiff.server_tile_prepare_progress', { seconds: elapsedSeconds })
    })
  }
  const fnStopPrepareStatusTimer = function () {
    if (!prepareTimer) return
    clearInterval(prepareTimer)
    prepareTimer = null
  }

  loadingTask.update({
    progress: null,
    mode: 'indeterminate',
    status: fnT('geotiff.server_tile_prepare_start')
  })
  fnUpdatePrepareStatus()
  prepareTimer = setInterval(fnUpdatePrepareStatus, 1000)

  let response
  try {
    response = await fetch('/api/geotiff-tiles/prepare', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sourcePath: resolvedPath,
        minZoom: prepareOptions.minZoom,
        maxZoom: prepareOptions.maxZoom,
        resampling: prepareOptions.resampling
      })
    })
  } catch (networkError) {
    const reason = String((networkError && networkError.message) || networkError || '').trim() || 'network_error'
    fnGeoTiffLog('GeoTIFF ortho server tile prepare network error', { error: reason })
    if (Swal && typeof Swal.fire === 'function') {
      Swal.fire({
        toast: true,
        position: 'bottom',
        icon: 'warning',
        title: fnT('geotiff.server_tile_prepare_failed'),
        text: fnTF('geotiff.server_tile_prepare_fallback', { reason: reason.slice(0, 220) }),
        showConfirmButton: false
      })
    }
    fnStopPrepareStatusTimer()
    return false
  }

  fnStopPrepareStatusTimer()

  let payload = null
  try {
    payload = await response.json()
  } catch (_jsonError) {}
  if (!response.ok) {
    const reason = String(payload && payload.error ? payload.error : ('HTTP ' + String(response.status))).trim()
    fnGeoTiffLog('GeoTIFF ortho server tile prepare http error', {
      status: response.status,
      error: reason
    })
    if (Swal && typeof Swal.fire === 'function') {
      Swal.fire({
        toast: true,
        position: 'bottom',
        icon: 'warning',
        title: fnT('geotiff.server_tile_prepare_failed'),
        text: fnTF('geotiff.server_tile_prepare_fallback', { reason: reason.slice(0, 220) }),
        showConfirmButton: false
      })
    }
    return false
  }

  const tileSet = payload && payload.tileSet && typeof payload.tileSet === 'object' ? payload.tileSet : null
  const tileUrlTemplate = String(tileSet && tileSet.tileUrlTemplate || '').trim()
  if (!tileUrlTemplate) return false

  const leafletBounds = fnBuildLeafletBoundsFromTileSet(tileSet)

  fnClearGeoTiffLayer(sourceType)
  const tileLayer = window.L.tileLayer(tileUrlTemplate, {
    pane: 'tiffPane',
    opacity: 1,
    tileSize: 256,
    maxZoom: 28,
    maxNativeZoom: Number.isFinite(Number(tileSet && tileSet.maxZoom)) ? Number(tileSet.maxZoom) : 22,
    crossOrigin: true,
    noWrap: true,
    bounds: (leafletBounds && leafletBounds.isValid && leafletBounds.isValid()) ? leafletBounds : undefined
  })
  sourceLayerGroup.addLayer(tileLayer)
  fnSetTiffLayer(tileLayer, sourceType)

  const overlayKey = sourceType === 'terrain' ? 'tiffDem' : 'tiffOrtho'
  const fallbackOverlayKey = sourceType === 'terrain' ? 'tiff' : 'tiff'
  const tiffEnabled = !(
    db &&
    db.settings &&
    db.settings.overlayVisible &&
    ((typeof db.settings.overlayVisible[overlayKey] === 'undefined')
      ? (db.settings.overlayVisible[fallbackOverlayKey] === false)
      : (db.settings.overlayVisible[overlayKey] === false))
  )
  if (tiffEnabled) sourceLayerGroup.addTo(map)
  else sourceLayerGroup.removeFrom(map)

  if (
    leafletBounds &&
    leafletBounds.isValid &&
    leafletBounds.isValid()
  ) {
    map.fitBounds(leafletBounds, { padding: [20, 20] })
  }

  if (sourceType === 'terrain') {
    const localTerrainState = fnBuildServerTerrainState(tileSet, file, fileSignature, {
      verticalShiftMeters: 0
    })
    let terrainCalibrationInfo = null
    if (localTerrainState && localTerrainState.hasCoverage) {
      if (autoAlignTerrain === false) {
        localTerrainState.setVerticalShiftMeters(0)
        localTerrainState.verticalShiftMeters = 0
        terrainCalibrationInfo = {
          applied: false,
          reason: 'auto_align_disabled',
          sampleCountRequested: TERRAIN_CALIBRATION_SAMPLE_COUNT,
          sampleCountBuilt: 0,
          sampleCountMatched: 0
        }
      } else {
        loadingTask.update({
          progress: null,
          mode: 'indeterminate',
          status: fnT('geotiff.loading')
        })
        try {
          terrainCalibrationInfo = await fnCalibrateTerrainShiftByProviders(localTerrainState, {
            sampleCount: TERRAIN_CALIBRATION_SAMPLE_COUNT
          })
          if (terrainCalibrationInfo && terrainCalibrationInfo.applied) {
            const appliedShift = localTerrainState.setVerticalShiftMeters(terrainCalibrationInfo.shiftMeters)
            localTerrainState.verticalShiftMeters = appliedShift
          } else {
            localTerrainState.setVerticalShiftMeters(0)
            localTerrainState.verticalShiftMeters = 0
          }
        } catch (calibrationError) {
          localTerrainState.setVerticalShiftMeters(0)
          localTerrainState.verticalShiftMeters = 0
          terrainCalibrationInfo = {
            applied: false,
            reason: 'calibration_error',
            error: String((calibrationError && calibrationError.message) || calibrationError || '')
          }
        }
      }
    }
    if (localTerrainState) {
      localTerrainState.calibration = terrainCalibrationInfo
      fnSetLocalTerrainState(Object.assign({}, localTerrainState, {
        providerKey: LOCAL_DEM_PROVIDER_KEY,
        sourceMode: 'terrain'
      }))
    } else {
      fnSetLocalTerrainState(null)
    }
    if (localTerrainState) {
      if (!db.settings || typeof db.settings !== 'object') db.settings = {}
      if (!db.settings.mapCursorElevation || typeof db.settings.mapCursorElevation !== 'object') {
        db.settings.mapCursorElevation = {}
      }
      db.settings.mapCursorElevation.provider = LOCAL_DEM_PROVIDER_KEY
    }
  }

  const activeDescriptor = buildGeoTiffDescriptor(Object.assign({}, file, {
    sourcePath: String(file && (file.sourcePath || file.path || '') || '')
  }), fileSignature)
  if (activeDescriptor) {
    try {
      await writeGeoTiffActiveDescriptor(activeDescriptor, geoTiffCacheOptions)
    } catch (_error) {}
  }
  fnSetTiffLastFile(file, sourceType)
  loadingTask.complete({
    status: fnT('geotiff.loaded'),
    progress: 100,
    mode: 'determinate'
  })
  return true
}

const loadGeoTiffFile = async function (file, options, {
    _ = window._,
    $ = window.jQuery,
    Swal = window.Swal,
    map = null,
    tiffItems = null,
    db = { settings: {} },
    fnSetTiffLayer = function () {},
    fnSetTiffLastFile = function () {},
    fnSetTiffParsedMemoryCache = function () {},
    fnGetTiffParsedMemoryCache = function () { return { signature: null, georaster: null } },
    fnT,
    fnTF,
    fnClampGeoTiffResolution,
    fnGetGeoTiffResolution,
    fnGeoTiffLog,
    fnClearGeoTiffLayer,
    fnGeoTiffCacheLoad,
    fnGeoTiffCacheReadIndex = async function () { return null },
    fnGeoTiffCacheWriteIndex = async function () {},
    fnReadFileAsArrayBufferWithProgress,
    fnGeoTiffFileSignature,
    fnGeoTiffParseCacheMissReason,
    fnGeoTiffCacheStore,
    fnDropGeoTiffRuntimeCache,
    GEOTIFF_ALLOWED_MIME_TYPES = [],
    GEOTIFF_MAX_FILE_BYTES = (2 * 1024 * 1024 * 1024),
    GEOTIFF_DEBUG_LOG = false
  } = {}) {

    const opts = _.assign({ resolution: null, mode: 'auto', sourceType: 'terrain', arrayBuffer: null, autoAlignTerrain: true, skipLoadingPopup: false }, options || {})
    const requestedSourceType = String(opts.sourceType || '').trim().toLowerCase()
    const sourceType = (requestedSourceType === 'ortho' || requestedSourceType === 'terrain')
      ? requestedSourceType
      : 'terrain'
    const sourceLayerGroup = (tiffItems && typeof tiffItems === 'object' && !Array.isArray(tiffItems) && tiffItems[sourceType])
      ? tiffItems[sourceType]
      : tiffItems
    if (!file || !map || !sourceLayerGroup) return false
    const ext = (String(file.name || '').split('.').pop() || '').toLowerCase()
    if (!['tif', 'tiff'].includes(ext)) {
      Swal.fire(fnT('geotiff.only_tif'))
      return false
    }
    const mimeType = String(file.type || '').toLowerCase()
    if (mimeType && !GEOTIFF_ALLOWED_MIME_TYPES.includes(mimeType)) {
      Swal.fire(fnTF('geotiff.unsupported_mime', { mime: mimeType }))
      return false
    }
    const sourcePath = String(file.sourcePath || '').trim()
    const nativePath = String(file.path || '').trim()
    const webkitRelativePath = String(file.webkitRelativePath || '').trim()
    const resolvedPath = sourcePath || nativePath || webkitRelativePath || String(file.name || '')
    const isElectronRuntime = fnIsElectronRuntime()
    const useClientGeoTiffCache = !isElectronRuntime
    const preferServerTileImport = isElectronRuntime && fnLooksLikeAbsoluteFilePath(resolvedPath)
    const fileExceedsClientLimit = Number(file.size || 0) > Number(GEOTIFF_MAX_FILE_BYTES || 0)
    if (!preferServerTileImport && fileExceedsClientLimit) {
      Swal.fire(fnT('geotiff.big_warning'))
      return false
    }
    const chosenResolution = fnClampGeoTiffResolution((opts && opts.resolution !== null && opts.resolution !== undefined) ? opts.resolution : fnGetGeoTiffResolution(file.size))
    const bigFileWarning = (file.size > (1024 * 1024 * 1024))
    const cacheIndexKey = 'portal-geotiff-cache-index-v1:' + sourceType
    const cacheKeyPrefix = 'portal-geotiff-cache-file-v1:' + sourceType + ':'
    const fileSignatureRaw = String(fnGeoTiffFileSignature(file) || '').trim()
    const fileSignature = sourceType + '::' + fileSignatureRaw
    console.log('[GeoTIFF][Path] load start', {
      name: String(file.name || ''),
      sourcePath: sourcePath,
      path: nativePath,
      webkitRelativePath: webkitRelativePath,
      resolvedPath: resolvedPath,
      hasFullPathHint: !!(sourcePath || nativePath),
      note: 'Browser file input often hides full path (fakepath/security)'
    })
    if (GEOTIFF_DEBUG_LOG) {
      fnGeoTiffLog(fnT('log.geotiff_load_start'), {
        name: String(file.name || ''),
        size: Number(file.size || 0),
        lastModified: Number(file.lastModified || 0),
        resolution: chosenResolution,
        sourceType: sourceType
      })
    }
    const loadingTask = startPortalLoadingTask({
      id: 'geotiff-load:' + sourceType + ':' + String(fileSignatureRaw || file.name || '') + ':' + String(Date.now()),
      title: fnT('geotiff.loading'),
      status: fnT('geotiff.cache_check'),
      progress: null,
      mode: 'indeterminate'
    })
    let phaseTimer = null
    try {
      const idbKeyval = (typeof window !== 'undefined' && window.idbKeyval && typeof window.idbKeyval.get === 'function')
        ? window.idbKeyval
        : null
      const geoTiffCacheOptions = {
        idbKeyval: idbKeyval,
        fnT: fnT,
        fnGeoTiffLog: fnGeoTiffLog,
        GEOTIFF_CACHE_INDEX_KEY: cacheIndexKey,
        GEOTIFF_CACHE_KEY_PREFIX: cacheKeyPrefix
      }
      if (preferServerTileImport) {
        const usedServerTiles = await fnTryLoadFromServerTiles({
          sourcePath: resolvedPath,
          map: map,
          sourceLayerGroup: sourceLayerGroup,
          fnSetTiffLayer: fnSetTiffLayer,
          fnSetTiffLastFile: fnSetTiffLastFile,
          fnClearGeoTiffLayer: fnClearGeoTiffLayer,
          file: file,
          fileSignature: fileSignature,
          sourceType: sourceType,
          db: db,
          Swal: Swal,
          fnT: fnT,
          fnTF: fnTF,
          fnGeoTiffLog: fnGeoTiffLog,
          loadingTask: loadingTask,
          autoAlignTerrain: opts.autoAlignTerrain !== false,
          geoTiffCacheOptions: geoTiffCacheOptions
        })
        if (usedServerTiles) return true
        if (fileExceedsClientLimit) {
          loadingTask.fail({
            status: fnT('geotiff.server_tile_prepare_failed')
          })
          return false
        }
      }

      if (!window.parseGeoraster || !window.GeoRasterLayer) {
        Swal.fire(fnT('geotiff.library_missing'))
        return false
      }

      if (sourceType === 'terrain') {
        fnSetLocalTerrainState(null)
      }
      fnClearGeoTiffLayer(sourceType)
      const fnCanReuseCachedTerrainMeta = function (terrainMeta, localTerrainState, shouldAutoAlignTerrain, resolvedMode) {
        if (!terrainMeta || typeof terrainMeta !== 'object') return false
        if (!localTerrainState || typeof localTerrainState !== 'object') return false
        if (String(resolvedMode || '').trim() !== 'terrain') return false
        if (String(terrainMeta.sourceMode || '').trim() !== 'terrain') return false
        if (!!terrainMeta.autoAlignTerrain !== !!shouldAutoAlignTerrain) return false
        if (String(terrainMeta.verticalReference || '').trim() !== String(localTerrainState.verticalReference || '').trim()) return false
        if (!!terrainMeta.verticalReferenceDetected !== !!localTerrainState.verticalReferenceDetected) return false
        return Number.isFinite(Number(terrainMeta.verticalShiftMeters))
      }
      const fnUpdateLoadingUi = function (percent, statusText) {
        const normalizedPercent = Number.isFinite(Number(percent))
          ? Math.max(0, Math.min(100, Number(percent)))
          : null
        loadingTask.update({
          progress: normalizedPercent,
          mode: normalizedPercent === null ? 'indeterminate' : 'determinate',
          status: statusText || ''
        })
      }
      const fnStartPhaseTimer = function (phaseLabel) {
        if (phaseTimer) {
          clearInterval(phaseTimer)
          phaseTimer = null
        }
        const startedAt = Date.now()
        const tick = function () {
          const elapsedSec = Math.max(0, Math.floor((Date.now() - startedAt) / 1000))
          fnUpdateLoadingUi(null, phaseLabel + ' (' + elapsedSec + ' mp)')
        }
        tick()
        phaseTimer = setInterval(tick, 1000)
      }
      const fnStopPhaseTimer = function () {
        if (phaseTimer) {
          clearInterval(phaseTimer)
          phaseTimer = null
        }
      }
      const fnCloneArrayBuffer = function (buffer) {
        if (!(buffer instanceof ArrayBuffer)) return null
        if (!(buffer.byteLength > 0)) return null
        try {
          return buffer.slice(0)
        } catch (_error) {
          try {
            return Uint8Array.from(new Uint8Array(buffer)).buffer
          } catch (_error2) {
            return null
          }
        }
      }
      fnUpdateLoadingUi(0, fnT('geotiff.cache_check'))
      const hasProvidedArrayBuffer = (opts.arrayBuffer instanceof ArrayBuffer)
      let arrayBuffer = hasProvidedArrayBuffer
        ? opts.arrayBuffer
        : (useClientGeoTiffCache ? await fnGeoTiffCacheLoad(file, fileSignature, geoTiffCacheOptions) : null)
      let cacheStoreArrayBuffer = null
      const cacheHit = useClientGeoTiffCache && (arrayBuffer instanceof ArrayBuffer)
      if (!(arrayBuffer instanceof ArrayBuffer)) {
        if (useClientGeoTiffCache) {
          fnGeoTiffLog(fnT('log.geotiff_filereader_cache_miss'))
        }
        if (
          isElectronRuntime &&
          (sourcePath || nativePath) &&
          window.electronAPI &&
          typeof window.electronAPI.readGeoTiffFile === 'function'
        ) {
          fnUpdateLoadingUi(null, fnT('geotiff.loading'))
          const readResult = await window.electronAPI.readGeoTiffFile(sourcePath || nativePath)
          arrayBuffer = (readResult && readResult.arrayBuffer instanceof ArrayBuffer)
            ? readResult.arrayBuffer
            : null
          if (!(arrayBuffer instanceof ArrayBuffer)) {
            throw new Error(fnT('geotiff.load_failed'))
          }
        } else {
          arrayBuffer = await fnReadFileAsArrayBufferWithProgress(file, function (prog) {
            if (!prog || !prog.lengthComputable || !(prog.total > 0)) return
            const percent = Math.round((prog.loaded / prog.total) * 100)
            fnUpdateLoadingUi(percent, fnTF('geotiff.reading', { percent: percent }))
          })
        }
        if (useClientGeoTiffCache) {
          cacheStoreArrayBuffer = fnCloneArrayBuffer(arrayBuffer)
        }
      } else {
        fnGeoTiffLog(fnT('log.geotiff_arraybuffer_cache_hit_skip_filereader'), { bytes: arrayBuffer.byteLength })
        fnUpdateLoadingUi(100, fnT('geotiff.cache_hit_read_skip'))
      }
      const signature = fileSignature
      const cachedTerrainMeta = useClientGeoTiffCache
        ? await readGeoTiffTerrainMeta(signature, geoTiffCacheOptions)
        : null
      let parseCacheHit = false
      let georaster = null
      const parsedCache = fnGetTiffParsedMemoryCache(sourceType)
      if (signature && parsedCache.signature === signature && parsedCache.georaster) {
        georaster = parsedCache.georaster
        parseCacheHit = true
        fnGeoTiffLog(fnT('log.geotiff_parse_cache_hit_memory'), {
          signature: signature,
          memorySignature: parsedCache.signature,
          hasGeoraster: !!parsedCache.georaster
        })
        fnUpdateLoadingUi(100, fnT('geotiff.cache_hit_parse_skip'))
      } else {
        fnGeoTiffLog(fnT('log.geotiff_parse_cache_miss'), {
          reason: fnGeoTiffParseCacheMissReason(signature),
          signature: signature,
          memorySignature: parsedCache ? parsedCache.signature : null,
          hasMemoryGeoraster: !!(parsedCache && parsedCache.georaster)
        })
        fnStartPhaseTimer(fnT('geotiff.phase_parse'))
        georaster = await window.parseGeoraster(arrayBuffer)
        fnGeoTiffLog(fnT('log.geotiff_parse_completed_main_thread'), { signature: signature })
        fnStopPhaseTimer()
        fnSetTiffParsedMemoryCache({
          signature: signature || null,
          georaster: georaster || null
        }, sourceType)
      }
      if (GEOTIFF_DEBUG_LOG) {
        const values = georaster && Array.isArray(georaster.values) ? georaster.values : []
        const bandStats = values.map(function (_b, idx) {
          const stats = fnBuildBandStats(georaster, idx)
          return {
            bandIndex: idx,
            finiteCount: stats.finiteCount,
            uniqueCount: stats.uniqueCount,
            min: stats.min,
            max: stats.max,
            range: stats.range,
            binaryLike: stats.binaryLike
          }
        })
        fnGeoTiffLog('Terrain debug: parsed georaster meta', {
          name: String(file && file.name || ''),
          width: Number(georaster && georaster.width),
          height: Number(georaster && georaster.height),
          projection: georaster ? georaster.projection : null,
          xmin: georaster ? georaster.xmin : null,
          xmax: georaster ? georaster.xmax : null,
          ymin: georaster ? georaster.ymin : null,
          ymax: georaster ? georaster.ymax : null,
          noDataValue: georaster ? georaster.noDataValue : null,
          bandCount: values.length,
          bands: bandStats
        })
      }
      const detectedVerticalReference = fnDetectTiffVerticalReference(georaster)
      const localTerrainState = fnBuildLocalTerrainState(georaster, file, signature, {
        verticalReferenceInfo: detectedVerticalReference
      })
      const modeDecision = fnResolveGeoTiffModeDecision(opts.mode, georaster, localTerrainState, file)
      const resolvedMode = sourceType === 'terrain'
        ? 'terrain'
        : (sourceType === 'ortho' ? 'ortho' : modeDecision.mode)
      let terrainCalibrationInfo = null
      if (resolvedMode === 'terrain' && localTerrainState && localTerrainState.hasCoverage) {
        const shouldAutoAlignTerrain = opts.autoAlignTerrain !== false
        if (!shouldAutoAlignTerrain) {
          localTerrainState.setVerticalShiftMeters(0)
          localTerrainState.verticalShiftMeters = 0
          terrainCalibrationInfo = {
            applied: false,
            reason: 'auto_align_disabled',
            sampleCountRequested: TERRAIN_CALIBRATION_SAMPLE_COUNT,
            sampleCountBuilt: 0,
            sampleCountMatched: 0
          }
          console.info('[GeoTIFF][Calib] Shift skipped', {
            name: String(file && file.name || ''),
            verticalReference: localTerrainState ? localTerrainState.verticalReference : null,
            verticalReferenceLabel: localTerrainState ? localTerrainState.verticalReferenceLabel : null,
            reason: 'auto_align_disabled'
          })
        } else if (fnCanReuseCachedTerrainMeta(cachedTerrainMeta, localTerrainState, shouldAutoAlignTerrain, resolvedMode)) {
          const cachedMetaValidation = await fnValidateCachedTerrainMeta(localTerrainState, cachedTerrainMeta, {
            sampleCount: Math.min(80, TERRAIN_CALIBRATION_SAMPLE_COUNT),
            maxShiftDeltaMeters: 3
          })
          if (cachedMetaValidation && cachedMetaValidation.reusable !== false) {
            const cachedShift = Math.round(Number(cachedTerrainMeta.verticalShiftMeters) * 10) / 10
            localTerrainState.setVerticalShiftMeters(cachedShift)
            localTerrainState.verticalShiftMeters = cachedShift
            terrainCalibrationInfo = {
              applied: !!(cachedTerrainMeta.calibrationApplied === true || Math.abs(cachedShift) > 0.0001),
              reason: 'cache_restored',
              shiftMeters: cachedShift,
              sampleCountRequested: Number(cachedTerrainMeta.sampleCountRequested) || 0,
              sampleCountBuilt: Number(cachedTerrainMeta.sampleCountBuilt) || 0,
              sampleCountMatched: Number(cachedTerrainMeta.sampleCountMatched) || 0,
              robust: (cachedTerrainMeta.robust && typeof cachedTerrainMeta.robust === 'object')
                ? Object.assign({}, cachedTerrainMeta.robust)
                : null
            }
            console.info('[GeoTIFF][Calib] Shift restored from cache', {
              name: String(file && file.name || ''),
              verticalReference: localTerrainState.verticalReference,
              verticalReferenceLabel: localTerrainState.verticalReferenceLabel,
              shiftMeters: cachedShift,
              validationReason: String((cachedMetaValidation && cachedMetaValidation.reason) || ''),
              source: 'cache'
            })
          } else if (cachedMetaValidation && cachedMetaValidation.calibrationInfo) {
            terrainCalibrationInfo = Object.assign({}, cachedMetaValidation.calibrationInfo)
            const appliedShift = (terrainCalibrationInfo && terrainCalibrationInfo.applied)
              ? localTerrainState.setVerticalShiftMeters(terrainCalibrationInfo.shiftMeters)
              : localTerrainState.setVerticalShiftMeters(0)
            localTerrainState.verticalShiftMeters = appliedShift
            console.info('[GeoTIFF][Calib] Cached shift rejected', {
              name: String(file && file.name || ''),
              verticalReference: localTerrainState.verticalReference,
              verticalReferenceLabel: localTerrainState.verticalReferenceLabel,
              cachedShiftMeters: Math.round(Number(cachedTerrainMeta.verticalShiftMeters || 0) * 10) / 10,
              appliedShiftMeters: appliedShift,
              validationReason: String((cachedMetaValidation && cachedMetaValidation.reason) || '')
            })
          } else if (shouldAutoAlignTerrain) {
            try {
              terrainCalibrationInfo = await fnCalibrateTerrainShiftByProviders(localTerrainState, {
                sampleCount: TERRAIN_CALIBRATION_SAMPLE_COUNT
              })
              if (terrainCalibrationInfo && terrainCalibrationInfo.applied) {
                const appliedShift = localTerrainState.setVerticalShiftMeters(terrainCalibrationInfo.shiftMeters)
                localTerrainState.verticalShiftMeters = appliedShift
              } else if (localTerrainState && typeof localTerrainState.setVerticalShiftMeters === 'function') {
                localTerrainState.setVerticalShiftMeters(0)
                localTerrainState.verticalShiftMeters = 0
              }
            } catch (calibrationError) {
              localTerrainState.setVerticalShiftMeters(0)
              localTerrainState.verticalShiftMeters = 0
              terrainCalibrationInfo = {
                applied: false,
                reason: 'calibration_error',
                error: String((calibrationError && calibrationError.message) || calibrationError || '')
              }
              console.warn('[GeoTIFF][Calib] Shift estimation error', {
                name: String(file && file.name || ''),
                error: terrainCalibrationInfo.error
              })
            }
          }
        } else if (shouldAutoAlignTerrain) {
          try {
            terrainCalibrationInfo = await fnCalibrateTerrainShiftByProviders(localTerrainState, {
              sampleCount: TERRAIN_CALIBRATION_SAMPLE_COUNT
            })
            if (terrainCalibrationInfo && terrainCalibrationInfo.applied) {
              const appliedShift = localTerrainState.setVerticalShiftMeters(terrainCalibrationInfo.shiftMeters)
              localTerrainState.verticalShiftMeters = appliedShift
            } else if (localTerrainState && typeof localTerrainState.setVerticalShiftMeters === 'function') {
              localTerrainState.setVerticalShiftMeters(0)
              localTerrainState.verticalShiftMeters = 0
            }
            if (terrainCalibrationInfo && terrainCalibrationInfo.applied) {
              console.groupCollapsed('[GeoTIFF][Calib] Shift estimated (' + String(file && file.name || '') + ')')
              console.info('[GeoTIFF][Calib] Summary', {
                verticalReference: localTerrainState.verticalReference,
                verticalReferenceLabel: localTerrainState.verticalReferenceLabel,
                shiftMeters: terrainCalibrationInfo.shiftMeters,
                sampleCountRequested: terrainCalibrationInfo.sampleCountRequested,
                sampleCountBuilt: terrainCalibrationInfo.sampleCountBuilt,
                sampleCountMatched: terrainCalibrationInfo.sampleCountMatched,
                quality: terrainCalibrationInfo.robust ? terrainCalibrationInfo.robust.quality : 'unknown',
                inlierPct: terrainCalibrationInfo.robust ? terrainCalibrationInfo.robust.inlierPct : null,
                madMeters: terrainCalibrationInfo.robust ? terrainCalibrationInfo.robust.madMeters : null,
                dominantBinCenterMeters: terrainCalibrationInfo.robust ? terrainCalibrationInfo.robust.dominantBinCenterMeters : null,
                dominantBinCount: terrainCalibrationInfo.robust ? terrainCalibrationInfo.robust.dominantBinCount : null
              })
              if (Array.isArray(terrainCalibrationInfo.providerStats) && terrainCalibrationInfo.providerStats.length) {
                console.table(terrainCalibrationInfo.providerStats)
              }
              if (Array.isArray(terrainCalibrationInfo.sampleRowsAll) && terrainCalibrationInfo.sampleRowsAll.length) {
                console.table(terrainCalibrationInfo.sampleRowsAll)
              }
              console.groupEnd()
            } else {
              console.info('[GeoTIFF][Calib] Shift skipped', {
                name: String(file && file.name || ''),
                verticalReference: localTerrainState ? localTerrainState.verticalReference : null,
                verticalReferenceLabel: localTerrainState ? localTerrainState.verticalReferenceLabel : null,
                verticalReferenceDetected: !!(localTerrainState && localTerrainState.verticalReferenceDetected),
                verticalReferenceSource: localTerrainState ? localTerrainState.verticalReferenceSource : null,
                reason: terrainCalibrationInfo ? terrainCalibrationInfo.reason : 'not_available',
                sampleCountRequested: terrainCalibrationInfo ? terrainCalibrationInfo.sampleCountRequested : TERRAIN_CALIBRATION_SAMPLE_COUNT,
                sampleCountBuilt: terrainCalibrationInfo ? terrainCalibrationInfo.sampleCountBuilt : 0,
                sampleCountMatched: terrainCalibrationInfo ? terrainCalibrationInfo.sampleCountMatched : 0
              })
            }
          } catch (calibrationError) {
            localTerrainState.setVerticalShiftMeters(0)
            localTerrainState.verticalShiftMeters = 0
            terrainCalibrationInfo = {
              applied: false,
              reason: 'calibration_error',
              error: String((calibrationError && calibrationError.message) || calibrationError || '')
            }
            console.warn('[GeoTIFF][Calib] Shift estimation error', {
              name: String(file && file.name || ''),
              error: terrainCalibrationInfo.error
            })
          }
        }
        localTerrainState.calibration = terrainCalibrationInfo
      }
      if (resolvedMode === 'terrain' && localTerrainState) {
        const storage = fnEstimateTerrainStorage(georaster, localTerrainState)
        const fileBytes = Number(file && file.size) || 0
        console.info('[GeoTIFF] DEM storage estimate', {
          name: String(file && file.name || ''),
          fileBytes: fileBytes,
          fileHuman: fnFormatBytes(fileBytes),
          decodedRasterBytes: storage.decodedRasterBytes,
          decodedRasterHuman: storage.decodedRasterHuman,
          selectedBandsBytes: storage.selectedBandsBytes,
          selectedBandsHuman: storage.selectedBandsHuman,
          estimatedIndexedDbBytes: storage.estimatedIndexedDbBytes,
          estimatedIndexedDbHuman: storage.estimatedIndexedDbHuman,
          selectedBandIndexes: storage.selectedBandIndexes,
          verticalReference: localTerrainState.verticalReference,
          verticalReferenceLabel: localTerrainState.verticalReferenceLabel,
          verticalShiftMeters: localTerrainState.verticalShiftMeters,
          calibrationApplied: !!(terrainCalibrationInfo && terrainCalibrationInfo.applied),
          calibrationReason: terrainCalibrationInfo ? terrainCalibrationInfo.reason : ''
        })
      }
      if (GEOTIFF_DEBUG_LOG) {
        const footprintMetrics = fnGetLatLngPolygonMetrics(localTerrainState ? localTerrainState.footprintLatLngs : [])
        fnGeoTiffLog('Terrain debug: local terrain candidate', {
          requestedMode: String(opts.mode || 'auto'),
          resolvedMode: resolvedMode,
          resolveReason: modeDecision.reason,
          hasLocalTerrainState: !!localTerrainState,
          hasCoverage: !!(localTerrainState && localTerrainState.hasCoverage),
          projectionEpsg: localTerrainState ? localTerrainState.projectionEpsg : null,
          projectionIsGeographic: localTerrainState ? localTerrainState.projectionIsGeographic : null,
          projectionTransformAvailable: localTerrainState ? localTerrainState.projectionTransformAvailable : null,
          projectionHasProj4: localTerrainState ? localTerrainState.projectionHasProj4 : null,
          projectionSourceCrsDefined: localTerrainState ? localTerrainState.projectionSourceCrsDefined : null,
          projectionSourceCrsAutoDefined: localTerrainState ? localTerrainState.projectionSourceCrsAutoDefined : null,
          verticalReference: localTerrainState ? localTerrainState.verticalReference : null,
          verticalReferenceLabel: localTerrainState ? localTerrainState.verticalReferenceLabel : null,
          verticalShiftMeters: localTerrainState ? localTerrainState.verticalShiftMeters : null,
          calibration: terrainCalibrationInfo || null,
          heightBandIndex: localTerrainState ? localTerrainState.heightBandIndex : null,
          maskBandIndex: localTerrainState ? localTerrainState.maskBandIndex : null,
          footprint: footprintMetrics
        })
      }
      let layer = null
      if (resolvedMode === 'ortho') {
        fnStartPhaseTimer(fnT('geotiff.phase_create_layer'))
        layer = new window.GeoRasterLayer({
          georaster: georaster,
          pane: 'tiffPane',
          opacity: 1,
          resolution: chosenResolution,
          resampleMethod: 'bilinear',
          debugLevel: 0,
          updateWhenIdle: true,
          keepBuffer: 32
        })
        sourceLayerGroup.addLayer(layer)
        fnSetTiffLayer(layer, sourceType)
        if (GEOTIFF_DEBUG_LOG) {
          fnGeoTiffLog('Terrain debug: ortho layer added', {
            mode: resolvedMode,
            resolution: chosenResolution
          })
        }
      } else {
        fnSetTiffLayer(null, sourceType)
        if (GEOTIFF_DEBUG_LOG) {
          fnGeoTiffLog('Terrain debug: ortho layer skipped', {
            mode: resolvedMode
          })
        }
      }
      const tiffEnabled = sourceType === 'terrain'
        ? !(
          db.settings &&
          db.settings.overlayVisible &&
          ((typeof db.settings.overlayVisible.tiffDem === 'undefined')
            ? (db.settings.overlayVisible.tiff === false)
            : (db.settings.overlayVisible.tiffDem === false))
        )
        : !(
          db.settings &&
          db.settings.overlayVisible &&
          ((typeof db.settings.overlayVisible.tiffOrtho === 'undefined')
            ? (db.settings.overlayVisible.tiff === false)
            : (db.settings.overlayVisible.tiffOrtho === false))
        )
      if (tiffEnabled) {
        sourceLayerGroup.addTo(map)
      } else {
        sourceLayerGroup.removeFrom(map)
      }
      const bounds = (layer && layer.getBounds) ? layer.getBounds() : null
      if (bounds && bounds.isValid && bounds.isValid() && resolvedMode === 'ortho') {
        map.fitBounds(bounds, { padding: [20, 20] })
        if (GEOTIFF_DEBUG_LOG) {
          fnGeoTiffLog('Terrain debug: ortho fitBounds applied', {
            southWest: bounds.getSouthWest ? bounds.getSouthWest() : null,
            northEast: bounds.getNorthEast ? bounds.getNorthEast() : null
          })
        }
      }
      if (localTerrainState && localTerrainState.hasCoverage && resolvedMode === 'terrain') {
        const hasSaneFootprint = fnAreLatLngPolygonSane(localTerrainState.footprintLatLngs)
        const footprintMetrics = fnGetLatLngPolygonMetrics(localTerrainState.footprintLatLngs)
        if (GEOTIFF_DEBUG_LOG) {
          fnGeoTiffLog('Terrain debug: terrain footprint check', {
            hasSaneFootprint: hasSaneFootprint,
            footprint: footprintMetrics
          })
        }
        if (window.L && typeof window.L.polygon === 'function' && hasSaneFootprint) {
          const footprintLayer = decorateLeafletPathDualDash(window.L.polygon(localTerrainState.footprintLatLngs, {
            color: '#00bcd4',
            weight: 2,
            dashArray: '6,4',
            fillColor: '#00bcd4',
            fillOpacity: 0.06,
            interactive: false
          }), { L: window.L })
          sourceLayerGroup.addLayer(footprintLayer)
          if (GEOTIFF_DEBUG_LOG) {
            fnGeoTiffLog('Terrain debug: footprint polygon added', {
              pointCount: footprintMetrics.count
            })
          }
        } else if (GEOTIFF_DEBUG_LOG) {
          fnGeoTiffLog('Terrain debug: footprint polygon skipped', {
            hasLeafletPolygon: !!(window.L && typeof window.L.polygon === 'function'),
            hasSaneFootprint: hasSaneFootprint
          })
        }
        const shouldAutoFit = fnShouldAutoFitTerrainFootprint(localTerrainState.footprintLatLngs)
        if (window.L && typeof window.L.latLngBounds === 'function' && hasSaneFootprint && shouldAutoFit) {
          const terrainBounds = window.L.latLngBounds(localTerrainState.footprintLatLngs)
          if (terrainBounds && terrainBounds.isValid && terrainBounds.isValid()) {
            map.fitBounds(terrainBounds, { padding: [20, 20] })
            if (GEOTIFF_DEBUG_LOG) {
              fnGeoTiffLog('Terrain debug: terrain fitBounds applied', {
                southWest: terrainBounds.getSouthWest ? terrainBounds.getSouthWest() : null,
                northEast: terrainBounds.getNorthEast ? terrainBounds.getNorthEast() : null
              })
            }
          }
        } else if (GEOTIFF_DEBUG_LOG) {
          fnGeoTiffLog('Terrain debug: terrain fitBounds skipped', {
            hasLeafletBounds: !!(window.L && typeof window.L.latLngBounds === 'function'),
            hasSaneFootprint: hasSaneFootprint,
            shouldAutoFit: shouldAutoFit,
            maxAllowedSpanDeg: 5
          })
        }
        const publishedLocalTerrainState = Object.assign({}, localTerrainState, {
          providerKey: LOCAL_DEM_PROVIDER_KEY,
          sourceMode: resolvedMode
        })
        if (Math.abs(Number(localTerrainState.verticalShiftMeters || 0)) < 0.0001 && typeof localTerrainState.sampleElevationRaw === 'function') {
          publishedLocalTerrainState.sampleElevation = function (lat, lng) {
            return localTerrainState.sampleElevationRaw(lat, lng)
          }
        }
        fnSetLocalTerrainState(publishedLocalTerrainState)
        if (GEOTIFF_DEBUG_LOG) {
          fnGeoTiffLog('Terrain debug: local terrain state activated', {
            providerKey: LOCAL_DEM_PROVIDER_KEY,
            sourceMode: resolvedMode,
            hasCoverage: localTerrainState.hasCoverage,
            verticalReference: localTerrainState.verticalReference,
            verticalReferenceLabel: localTerrainState.verticalReferenceLabel,
            verticalShiftMeters: localTerrainState.verticalShiftMeters,
            calibration: terrainCalibrationInfo || null
          })
        }
        if (!db.settings || typeof db.settings !== 'object') db.settings = {}
        if (!db.settings.mapCursorElevation || typeof db.settings.mapCursorElevation !== 'object') {
          db.settings.mapCursorElevation = {}
        }
        db.settings.mapCursorElevation.provider = LOCAL_DEM_PROVIDER_KEY
      } else {
        if (GEOTIFF_DEBUG_LOG) {
          fnGeoTiffLog('Terrain debug: local terrain state not activated', {
            hasLocalTerrainState: !!localTerrainState,
            hasCoverage: !!(localTerrainState && localTerrainState.hasCoverage),
            resolvedMode: resolvedMode
          })
        }
        if (sourceType === 'terrain') {
          fnSetLocalTerrainState(null)
        }
        if (sourceType === 'terrain' && db.settings && db.settings.mapCursorElevation && db.settings.mapCursorElevation.provider === LOCAL_DEM_PROVIDER_KEY) {
          const fallbackProviderOrder = fnBuildTerrainCalibrationProviderOrder()
          db.settings.mapCursorElevation.provider = fallbackProviderOrder[0] || 'openhikingHungary'
        }
      }
      let cacheStoreSucceeded = false
      let verifiedCacheEntry = null
      if (useClientGeoTiffCache) {
        cacheStoreSucceeded = !hasProvidedArrayBuffer && cacheHit
        if (!cacheHit) {
          fnUpdateLoadingUi(100, fnT('geotiff.phase_cache_store'))
          cacheStoreSucceeded = !!(await fnGeoTiffCacheStore(file, cacheStoreArrayBuffer || arrayBuffer, fileSignature, geoTiffCacheOptions))
        } else {
          fnGeoTiffLog(fnT('log.geotiff_skip_store_already_from_cache'))
          if (hasProvidedArrayBuffer && signature) {
            cacheStoreSucceeded = !!(await readGeoTiffCacheEntry(signature, geoTiffCacheOptions))
          }
        }
        if (cacheStoreSucceeded && signature) {
          verifiedCacheEntry = await readGeoTiffCacheEntry(signature, geoTiffCacheOptions)
          cacheStoreSucceeded = !!verifiedCacheEntry
          if (!cacheStoreSucceeded) {
            console.warn('[GeoTIFF][Cache] store verification failed', {
              sourceType: sourceType,
              signature: signature,
              name: String(file && file.name || '')
            })
          }
        } else if (signature) {
          console.warn('[GeoTIFF][Cache] store unavailable for restore', {
            sourceType: sourceType,
            signature: signature,
            name: String(file && file.name || ''),
            hasProvidedArrayBuffer: hasProvidedArrayBuffer,
            cacheHit: cacheHit
          })
        }
      }
      if (useClientGeoTiffCache && signature && resolvedMode === 'terrain' && localTerrainState && localTerrainState.hasCoverage) {
        await writeGeoTiffTerrainMeta(signature, {
          sourceMode: 'terrain',
          autoAlignTerrain: opts.autoAlignTerrain !== false,
          verticalReference: String(localTerrainState.verticalReference || ''),
          verticalReferenceLabel: String(localTerrainState.verticalReferenceLabel || ''),
          verticalReferenceDetected: !!localTerrainState.verticalReferenceDetected,
          verticalReferenceSource: String(localTerrainState.verticalReferenceSource || ''),
          verticalShiftMeters: Math.round(Number(localTerrainState.verticalShiftMeters || 0) * 10) / 10,
          calibrationApplied: !!(terrainCalibrationInfo && terrainCalibrationInfo.applied),
          calibrationReason: String((terrainCalibrationInfo && terrainCalibrationInfo.reason) || ''),
          sampleCountRequested: Number(terrainCalibrationInfo && terrainCalibrationInfo.sampleCountRequested) || 0,
          sampleCountBuilt: Number(terrainCalibrationInfo && terrainCalibrationInfo.sampleCountBuilt) || 0,
          sampleCountMatched: Number(terrainCalibrationInfo && terrainCalibrationInfo.sampleCountMatched) || 0,
          robust: (terrainCalibrationInfo && terrainCalibrationInfo.robust && typeof terrainCalibrationInfo.robust === 'object')
            ? Object.assign({}, terrainCalibrationInfo.robust)
            : null,
          cachedAt: Date.now()
        }, geoTiffCacheOptions)
      }
      const activeDescriptor = buildGeoTiffDescriptor(Object.assign({}, file, {
        sourcePath: String(file.sourcePath || file.path || file.webkitRelativePath || '')
      }), signature)
      let activeDescriptorWriteSucceeded = false
      if (activeDescriptor && (useClientGeoTiffCache ? cacheStoreSucceeded : isElectronRuntime)) {
        activeDescriptorWriteSucceeded = !!(await writeGeoTiffActiveDescriptor(activeDescriptor, geoTiffCacheOptions))
      } else if (activeDescriptor && useClientGeoTiffCache) {
        console.warn('[GeoTIFF][Restore] active descriptor skipped because cache is not persistent', {
          sourceType: sourceType,
          signature: signature,
          sourcePath: String(activeDescriptor.sourcePath || '')
        })
      }
      if (activeDescriptor) {
        const writeWasAttempted = (useClientGeoTiffCache ? cacheStoreSucceeded : isElectronRuntime) === true
        if (writeWasAttempted && !activeDescriptorWriteSucceeded) {
          console.warn('[GeoTIFF][Restore] active descriptor write failed', {
            sourceType: sourceType,
            signature: signature,
            activeDescriptor: activeDescriptor
          })
        }
      }
      fnStopPhaseTimer()
      loadingTask.complete({
        status: fnT('geotiff.loaded'),
        progress: 100,
        mode: 'determinate'
      })
      const details = []
      if (bigFileWarning) {
        details.push(fnT('geotiff.detail_large_file'))
      }
      if (georaster && georaster.width && georaster.height) {
        details.push(fnTF('geotiff.detail_size', { width: georaster.width, height: georaster.height }))
      }
      details.push(fnTF('geotiff.detail_sampling', { resolution: chosenResolution }))
      details.push(cacheHit ? fnT('geotiff.detail_source_cache') : fnT('geotiff.detail_source_file'))
      details.push(parseCacheHit ? fnT('geotiff.detail_parse_cache') : fnT('geotiff.detail_parse_fresh'))
      if (resolvedMode === 'terrain' && localTerrainState && localTerrainState.hasCoverage) {
        details.push('DEM/DSM local aktiv')
        details.push('Vertikális referencia: ' + String(localTerrainState.verticalReferenceLabel || fnGetTiffVerticalReferenceLabel(localTerrainState.verticalReference)))
        if (terrainCalibrationInfo && terrainCalibrationInfo.applied) {
          const shiftText = Number(localTerrainState.verticalShiftMeters || 0).toFixed(1)
          const qualityText = String(terrainCalibrationInfo.robust && terrainCalibrationInfo.robust.quality || '')
          const inlierText = Number(terrainCalibrationInfo.robust && terrainCalibrationInfo.robust.inlierPct || 0).toFixed(1)
          details.push('Auto shift: ' + shiftText + ' m (' + qualityText + ', inlier: ' + inlierText + '%)')
        } else if (terrainCalibrationInfo && terrainCalibrationInfo.reason) {
          if (terrainCalibrationInfo.reason === 'vertical_reference_detected') {
            details.push('Auto shift kihagyva: explicit TIFF vertikális referencia')
          } else if (terrainCalibrationInfo.reason === 'auto_align_disabled') {
            details.push('Auto shift kikapcsolva')
          } else {
            details.push('Auto shift kihagyva: ' + String(terrainCalibrationInfo.reason))
          }
        }
        if (localTerrainState.projectionIsGeographic === false && localTerrainState.projectionTransformAvailable === false) {
          details.push('CRS transzformacio hianyzik')
        }
      } else if (resolvedMode === 'ortho') {
        details.push('Orto overlay aktiv')
      }
      if (details.length) {
        Swal.fire({
          toast: true,
          position: 'bottom',
          icon: 'success',
          title: fnT('geotiff.loaded'),
          text: details.join(' | '),
          showConfirmButton: false
        })
      }
      fnSetTiffLastFile(file, sourceType)
      return true
    } catch (error) {
      if (phaseTimer) {
        clearInterval(phaseTimer)
        phaseTimer = null
      }
      if (String((error && (error.message || error)) || '').toLowerCase().includes('out of memory')) {
        fnDropGeoTiffRuntimeCache()
      }
      if (sourceType === 'terrain') {
        fnSetLocalTerrainState(null)
      }
      loadingTask.fail({
        status: String((error && (error.message || error)) || fnT('geotiff.load_failed'))
      })
      console.error(fnT('log.geotiff_import_error'), error)
      const msg = String((error && (error.message || error)) || '')
      if (msg.toLowerCase().includes('security') || msg.toLowerCase().includes('.ovr')) {
        Swal.fire(fnT('geotiff.load_error_ovr'))
        return false
      }
      Swal.fire(fnT('geotiff.load_failed'))
      return false
    }
}

export {
  loadGeoTiffFile
}
